<template>
  <div class="docs-page">
    <!-- ── Sidebar ── -->
    <aside class="docs-sidebar">
      <div class="sidebar-head">
        <div class="sidebar-brand">
          <span class="sb-pos">POS</span><span class="sb-wave">WAVE</span>
        </div>
        <span class="sidebar-sub">Centro de Ayuda</span>
      </div>

      <div class="search-wrap">
        <i class="bi bi-search search-icon"></i>
        <input
          v-model="search"
          type="text"
          class="search-input"
          placeholder="Buscar módulo…"
          autocomplete="off"
        />
        <button v-if="search" class="search-clear" @click="search = ''">
          <i class="bi bi-x"></i>
        </button>
      </div>

      <nav class="docs-nav">
        <template v-for="group in navGroups" :key="group.label">
          <span class="nav-group-label">{{ group.label }}</span>
          <a
            v-for="mod in group.mods"
            :key="mod.id"
            class="nav-item"
            :class="{ active: activeId === mod.id }"
            @click="setActive(mod.id)"
          >
            <div class="nav-icon"><i :class="'bi ' + mod.icon"></i></div>
            <span>{{ mod.title }}</span>
          </a>
        </template>
        <p v-if="navGroups.length === 0" class="no-results">
          Sin resultados para "{{ search }}"
        </p>
      </nav>
    </aside>

    <!-- ── Main ── -->
    <main class="docs-main" ref="mainEl">
      <template v-for="mod in modules" :key="mod.id">
        <div v-if="activeId === mod.id" class="docs-module">

          <!-- Hero -->
          <header class="mod-hero">
            <div class="mod-icon-ring">
              <i :class="'bi ' + mod.icon"></i>
            </div>
            <div class="mod-hero-text">
              <span class="mod-eyebrow">MÓDULO</span>
              <h1 class="mod-title">{{ mod.title }}</h1>
              <p class="mod-desc">{{ mod.description }}</p>
            </div>
          </header>

          <!-- Quick-jump chips -->
          <div class="section-chips" v-if="mod.sections.length > 1">
            <button
              v-for="sec in mod.sections"
              :key="sec.title"
              class="section-chip"
              @click="scrollToSection(sec.title)"
            >{{ sec.title }}</button>
          </div>

          <!-- Sections -->
          <div
            v-for="(section, si) in mod.sections"
            :key="section.title"
            :id="sectionId(section.title)"
            class="docs-section"
            :class="{ 'not-first': si > 0 }"
          >
            <div class="section-header">
              <h2 class="section-title">{{ section.title }}</h2>
              <span class="section-badge">
                {{ section.steps.length }}&thinsp;{{ section.steps.length === 1 ? 'paso' : 'pasos' }}
              </span>
            </div>

            <p v-if="section.intro" class="section-intro">{{ section.intro }}</p>

            <div class="steps-list">
              <div
                v-for="(step, idx) in section.steps"
                :key="idx"
                class="step-item"
                :class="{ 'has-callout': step.tip || step.warning }"
              >
                <div class="step-track">
                  <div class="step-num">{{ idx + 1 }}</div>
                  <div v-if="idx < section.steps.length - 1" class="step-line"></div>
                </div>
                <div class="step-body">
                  <p class="step-text">{{ step.text }}</p>
                  <div v-if="step.tip" class="callout tip">
                    <i class="bi bi-lightbulb-fill"></i>
                    <span>{{ step.tip }}</span>
                  </div>
                  <div v-if="step.warning" class="callout warn">
                    <i class="bi bi-exclamation-triangle-fill"></i>
                    <span>{{ step.warning }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="section.note" class="section-note">
              <i class="bi bi-info-circle-fill"></i>
              <span>{{ section.note }}</span>
            </div>
          </div>

        </div>
      </template>
    </main>
  </div>
</template>

<script>
import { setPageTitle } from '@/components/pageMetadata';

export default {
  name: 'DocumentacionPage',

  computed: {
    userRole() {
      return localStorage.getItem('role') || '';
    },
    navGroups() {
      const q    = this.search.toLowerCase().trim();
      const role = this.userRole;
      const groups = [
        { label: 'Cajero',        ids: ['ventas'] },
        { label: 'Operaciones',   ids: ['admin-ventas','cotizaciones','compras','produccion'] },
        { label: 'Contactos',     ids: ['clientes','proveedores'] },
        { label: 'Catálogo',      ids: ['productos','categorias','unidad-medida','promociones-producto','promociones-categorias'] },
        { label: 'Finanzas',      ids: ['cuentas-cobrar','cuentas-pagar','arqueo','devoluciones','notas-credito'] },
        { label: 'Reportes',      ids: ['reportes','reporte-anulaciones','nomina','libros','alertas-inventario'] },
        { label: 'Sistema',       ids: ['empleados','configuracion','plan-facturacion'] },
      ];
      return groups.map(g => ({
        label: g.label,
        mods: this.modules.filter(m =>
          g.ids.includes(m.id) &&
          // Módulos exclusivos CEO (role 4) no aparecen para otros roles
          (!m.ceoOnly || role === '4') &&
          (!q || m.title.toLowerCase().includes(q) || m.description.toLowerCase().includes(q) ||
           m.sections.some(s => s.title.toLowerCase().includes(q)))
        ),
      })).filter(g => g.mods.length > 0);
    },
  },

  data() {
    return {
      search: '',
      activeId: 'ventas',
      modules: [

        /* ─────────────────────────── CAJERO ─────────────────────────── */
        {
          id: 'ventas', icon: 'bi-cash-stack', title: 'Ventas',
          description: 'La pantalla principal de cobro. Aquí el cajero registra cada venta del día: agrega productos, aplica descuentos, selecciona al cliente, aplica notas de crédito y cobra.',
          sections: [
            {
              title: 'Las partes de la pantalla',
              intro: 'La pantalla está dividida en secciones. Conocerlas te ayuda a moverte rápido:',
              steps: [
                { text: 'Parte izquierda — Tabla de venta: aquí aparecen los artículos que vas agregando. Cada fila muestra el código, nombre, cantidad, precio unitario y subtotal. Al fondo aparece el TOTAL de la venta.' },
                { text: 'Arriba de la tabla — Cliente: muestra a quién le estás vendiendo. Por defecto dice "Consumidor final". Si el cliente tiene tipo especial (VIP, Tercera Edad, Mayorista), aparece un badge de color junto a su nombre con el descuento que aplica.', tip: 'Cuando seleccionas un cliente de Tercera Edad, el sistema aplica su descuento automáticamente a todos los productos. Verás un indicador "Ahorrado: L. X" en la parte inferior de la tabla.' },
                { text: 'Parte derecha arriba — Campo de búsqueda y teclado numérico: en el recuadro blanco escribes el nombre o código del producto y presionas Enter. El teclado numérico sirve para escribir cantidades o códigos sin teclado físico.' },
                { text: 'Parte derecha abajo — Botones Abrir/Cerrar caja: "Abrir caja" aparece cuando la caja está cerrada; "Cerrar caja" (en rojo) cuando ya está abierta.' },
                { text: 'Barra inferior — Botones de acción: una fila de botones con todos los atajos de teclado disponibles.', warning: 'Si al entrar todos los botones están en gris, la caja no está abierta. Haz clic en "Abrir caja" primero.' },
                { text: 'Banner de notificación: si el Administrador anula una venta tuya mientras estás en la caja, aparece un aviso automático en pantalla con el número de factura y el motivo. Ciérralo cuando lo hayas leído.' },
              ],
            },
            {
              title: 'Cómo abrir y cerrar la caja',
              intro: 'Antes de vender, la caja debe estar abierta. Al terminar el turno, hay que cerrarla:',
              steps: [
                { text: 'Para abrir: haz clic en "Abrir caja" (parte derecha). Escribe el monto de efectivo disponible para dar cambio (el fondo de caja) y confirma.' },
                { text: 'Una vez abierta, todos los botones se activan y el cajero puede empezar a registrar ventas.', tip: 'Si "Abrir caja" no aparece, la caja ya fue abierta por alguien más. Solo puede haber una caja abierta a la vez.' },
                { text: 'Para cerrar: haz clic en "Cerrar caja" (botón rojo). El sistema muestra cuánto debería haber en efectivo y transferencia según las ventas del día.' },
                { text: 'Cuenta el dinero físico en caja en ese momento y escríbelo en el campo correspondiente.' },
                { text: 'Confirma el cierre. El sistema registra el arqueo automáticamente.', warning: 'Una vez cerrada no puedes editar el monto. Cuenta bien el dinero antes de confirmar.' },
              ],
            },
            {
              title: 'Los botones de abajo y sus atajos de teclado',
              intro: 'Todos los botones de la barra inferior tienen un atajo. Aprenderlos hace el cobro mucho más rápido:',
              steps: [
                { text: '[F2] Buscar producto — abre un modal con buscador para encontrar productos por nombre o código. Úsalo cuando el campo de búsqueda rápida no es suficiente.' },
                { text: '[F3] Buscar/agregar cliente — abre la lista de clientes para seleccionar a quién le estás vendiendo. También puedes crear un cliente nuevo desde ahí sin salir de la venta.' },
                { text: '[F4] Eliminar item — borra de la tabla el producto seleccionado (el que tiene la fila resaltada). Pide clave de administrador para confirmar.', warning: 'Sin la clave del administrador no se puede eliminar. Pídela cuando la necesites.' },
                { text: '[F5] Limpiar pantalla — borra TODOS los productos de una vez. Pide clave de administrador. Úsalo solo para cancelar una venta completa.' },
                { text: '[F6] Guardar venta — guarda la venta actual y deja la pantalla libre para atender a otro cliente. La venta no se pierde.' },
                { text: '[F7] Recuperar Venta — abre la lista de ventas guardadas con F6. Selecciona la que quieres retomar y los productos vuelven a la tabla.' },
                { text: '[F8] Descuento Producto — aplica descuento al producto seleccionado. Primero haz doble clic sobre la fila del producto para seleccionarlo, luego presiona F8. Solo aparece si tu rol tiene ese permiso.' },
                { text: '[F9] Descuento General — aplica un descuento a toda la venta. Solo aparece si tu rol tiene ese permiso.', tip: 'Los botones F8 y F9 no aparecen si tu usuario no tiene permiso de descuentos. Es intencional — si los necesitas, pídele al administrador que habilite ese permiso en tu rol.' },
                { text: 'Aplicar NC — abre el modal de Notas de Crédito disponibles del cliente. Puedes seleccionar una o varias NCs para descontar del total de la venta. Solo aparece si el plan tiene Devoluciones habilitadas y tu rol tiene el permiso.' },
                { text: '[F12] Registrar Pago — abre la ventana de cobro. Elige el método: Efectivo, Transferencia, Mixto (efectivo + transferencia) o Crédito. Crédito solo aparece si el plan tiene Cuentas por Cobrar. Si el cliente tiene límite de crédito configurado o facturas vencidas, el sistema pedirá clave de administrador para autorizar antes de procesar.', tip: 'Si aparece la pantalla de "crédito bloqueado", llama al administrador para que ingrese su clave y autorice la venta. Sin esa clave, la venta a crédito no puede procesarse.' },
                { text: 'Cotizaciones pendientes — muestra las cotizaciones aceptadas listas para facturar. Solo aparece si el plan tiene Cotizaciones habilitadas.' },
                { text: '[ALT+S] Salir — cierra tu sesión. El sistema pide confirmación antes de cerrar.' },
              ],
            },
            {
              title: 'Cómo hacer una venta paso a paso',
              intro: 'El flujo completo de cobro:',
              steps: [
                { text: 'Verifica que la caja esté abierta. Si los botones están en gris, ábrela primero.' },
                { text: 'Escribe el nombre o código del producto en el campo de búsqueda (parte derecha arriba) y presiona Enter. El producto aparece en la tabla con cantidad 1.' },
                { text: 'Si el lector de barras está conectado, apunta al código de barras del producto — el lector lo escribe automáticamente en el campo y presiona Enter solo.' },
                { text: 'Para agregar más de 1 unidad de golpe: escribe la cantidad, luego el asterisco (*), luego el código del producto y presiona Enter. Ejemplo: "3*JUGO01" agrega 3 unidades del producto JUGO01.' },
                { text: 'Repite para cada artículo.' },
                { text: 'Si el cliente quiere factura con sus datos, presiona F3 y selecciónalo. Si tiene Nota de Crédito disponible y quiere usarla, haz clic en "Aplicar NC" y selecciona la NC.' },
                { text: 'Presiona F12 para cobrar. Elige el método de pago: Efectivo, Transferencia o Mixto.' },
                { text: 'Si paga en efectivo: escribe el monto recibido. El sistema calcula el vuelto automáticamente.' },
                { text: 'Si paga mitad efectivo y mitad transferencia: elige Mixto, escribe cada monto por separado.' },
                { text: 'Confirma el pago. La venta queda registrada y se genera el recibo imprimible.', tip: 'Desde la pantalla de confirmación puedes imprimir el recibo directamente.' },
              ],
            },
            {
              title: 'Cómo seleccionar un producto en la tabla',
              intro: 'Seleccionar un producto es necesario para eliminarlo (F4) o aplicarle descuento (F8):',
              steps: [
                { text: 'Haz doble clic sobre la fila del producto en la tabla. La fila cambia de color para indicar que está seleccionada.' },
                { text: 'Ahora puedes presionar F4 para eliminarlo o F8 para aplicarle descuento.' },
                { text: 'Para deseleccionarlo, haz doble clic otra vez sobre la misma fila.', tip: 'Solo puedes tener un producto seleccionado a la vez.' },
              ],
            },
            {
              title: 'Cómo aplicar una Nota de Crédito en la venta',
              steps: [
                { text: 'Primero selecciona al cliente que tiene la Nota de Crédito disponible (F3). Las NCs son por cliente.' },
                { text: 'Agrega los productos normalmente.' },
                { text: 'Haz clic en el botón "Aplicar NC" del footer. Se abre el modal con todas las NCs disponibles del cliente.' },
                { text: 'Selecciona una o varias NCs haciendo clic sobre ellas (se resaltan en dorado). Puedes seleccionar varias si el cliente tiene más de una.' },
                { text: 'Haz clic en "Aplicar NCs seleccionadas". El total del carrito se reduce automáticamente.', warning: 'Verifica que el cliente correcto esté seleccionado antes de aplicar. Las NCs son intransferibles entre clientes.' },
                { text: 'Si la NC cubre el 100% del total, el monto queda en L. 0.00 y puedes confirmar el pago sin cobrar nada.' },
                { text: 'Si te equivocas de NC, puedes volver al modal, deseleccionarla y elegir otra. El sistema recalcula desde cero.' },
              ],
            },
            {
              title: 'Cómo guardar una venta y atender a otro cliente',
              steps: [
                { text: 'Si tienes productos en la tabla pero el cliente necesita esperar (busca más artículos, va al carro, etc.), presiona F6 o "Guardar venta".' },
                { text: 'El sistema puede pedirte el nombre del cliente para identificar la venta guardada. Escríbelo o déjalo vacío.' },
                { text: 'La tabla queda vacía y puedes atender al siguiente cliente.' },
                { text: 'Cuando el primer cliente regrese, presiona F7 o "Recuperar Venta". Aparece la lista con todas las ventas guardadas.' },
                { text: 'Haz clic en la venta del cliente. Los productos regresan exactamente como los dejaste.', tip: 'Puedes tener varias ventas guardadas al mismo tiempo y recuperarlas en cualquier orden.' },
              ],
            },
          ],
        },

        {
          id: 'cotizaciones', icon: 'bi-file-earmark-text', title: 'Cotizaciones',
          description: 'Una cotización es un presupuesto formal que le entregas al cliente antes de confirmar la compra. Disponible para Administrador y CEO. Cuando el cliente acepte, se convierte en venta con un solo clic — sin volver a ingresar los productos.',
          sections: [
            {
              title: 'Qué verás al entrar a cotizaciones',
              intro: 'Al abrir la pantalla encontrarás:',
              steps: [
                { text: 'Lista de cotizaciones existentes: muestra todas las cotizaciones creadas con su número, cliente, fecha, monto total y estado (Pendiente, Aceptada, Vencida).' },
                { text: 'Botón "Nueva Cotización" (parte superior derecha o botón "+"): abre el formulario para crear una nueva.' },
                { text: 'Filtros de búsqueda: puedes buscar por nombre de cliente, número de cotización o rango de fechas.' },
                { text: 'Botones de acción en cada fila: Ver detalle, Editar, Convertir a venta y Eliminar.', tip: 'Una cotización vencida no puede convertirse en venta. Crea una nueva con fecha actualizada.' },
              ],
            },
            {
              title: 'Cómo crear una cotización',
              steps: [
                { text: 'Haz clic en "Nueva Cotización". Se abrirá el formulario.' },
                { text: 'Selecciona el cliente. Si el cliente no existe, puedes crearlo desde aquí con el botón "Nuevo cliente".' },
                { text: 'Ingresa la fecha de vencimiento — hasta qué día será válida esta cotización.', warning: 'La fecha de vencimiento debe ser hoy o posterior. No puedes poner una fecha pasada.' },
                { text: 'Agrega los productos uno a uno: búscalos por nombre o código, ingresa la cantidad. El sistema calcula subtotales automáticamente.' },
                { text: 'Si hay descuentos o notas para el cliente, agrégalos en los campos correspondientes.' },
                { text: 'Revisa el total y haz clic en "Guardar". La cotización quedará en estado Pendiente.' },
                { text: 'Puedes imprimirla o exportarla para enviársela al cliente.', tip: 'El cliente recibe un documento con los precios y la fecha límite de validez.' },
              ],
            },
            {
              title: 'Cómo convertir una cotización en venta',
              steps: [
                { text: 'Cuando el cliente confirme que acepta, busca la cotización en la lista.' },
                { text: 'Haz clic en el botón "Convertir a venta" (ícono de flecha o carrito).' },
                { text: 'Revisa los productos y el total. Si todo está correcto, confirma.' },
                { text: 'El sistema te llevará al flujo normal de cobro: elige el método de pago y confirma.', tip: 'Al convertir, el inventario de los productos se descuenta automáticamente, igual que en cualquier venta.' },
              ],
            },
            {
              title: 'Lo que puede hacer el Administrador y el CEO',
              steps: [
                { text: 'Ambos pueden crear, editar, cancelar y convertir cotizaciones en ventas.' },
                { text: 'El CEO puede filtrar por sucursal para ver las cotizaciones de todos los puntos de venta.' },
                { text: 'Ambos pueden descargar el PDF de cualquier cotización para enviárselo al cliente.' },
                { text: 'El cajero puede ver las cotizaciones pendientes desde la pantalla de ventas (botón "Cotizaciones pendientes") pero no puede crearlas.', tip: 'Las cotizaciones vencidas no se pueden convertir en venta. Crea una nueva con fecha actualizada si el cliente acepta después de la fecha de vencimiento.' },
              ],
            },
          ],
        },

        {
          id: 'arqueo', icon: 'bi-safe', title: 'Arqueo de Caja',
          description: 'El arqueo es el proceso de verificar que el dinero físico en la caja coincida con lo que el sistema registró como cobrado. Solo el Administrador y el CEO pueden abrir y cerrar la caja. Es obligatorio abrirla antes de que el cajero empiece a vender.',
          sections: [
            {
              title: 'Qué verás al entrar a arqueo de caja',
              intro: 'La pantalla muestra el estado actual de la caja del día:',
              steps: [
                { text: 'Estado de la caja: indica si está Abierta o Cerrada, la hora de apertura y el monto con que se abrió.' },
                { text: 'Resumen del turno: total vendido en efectivo, total en tarjeta y total de ventas del día.' },
                { text: 'Botón "Abrir caja": visible cuando la caja está cerrada. Solo disponible para Administrador y CEO.' },
                { text: 'Botón "Cerrar caja" o "Cierre de turno": visible cuando la caja está abierta.', tip: 'Abre la caja antes de que empiece el turno de ventas. Sin caja abierta el cajero no puede registrar ventas.' },
              ],
            },
            {
              title: 'Cómo abrir la caja al inicio del día',
              intro: 'Solo Administrador y CEO pueden hacer esto:',
              steps: [
                { text: 'Ve a "Finanzas" → "Arqueo de Caja" en el menú lateral.' },
                { text: 'Haz clic en "Abrir caja".' },
                { text: 'Cuenta el dinero físico disponible en la caja para dar cambio. Ingresa ese monto exacto.' },
                { text: 'Confirma. La caja queda abierta y el cajero ya puede empezar a registrar ventas.' },
              ],
            },
            {
              title: 'Cómo cerrar la caja al final del turno',
              intro: 'Solo Administrador y CEO pueden hacer el cierre:',
              steps: [
                { text: 'Ve a "Finanzas" → "Arqueo de Caja" en el menú lateral.' },
                { text: 'Haz clic en "Cerrar caja" o "Cierre de turno".' },
                { text: 'El sistema te mostrará cuánto debería haber en caja según las ventas registradas.' },
                { text: 'Cuenta el dinero físico que tienes y escribe ese monto en el campo correspondiente.' },
                { text: 'El sistema compara los dos montos y te muestra si hay diferencia (sobrante o faltante).' },
                { text: 'Revisa el resumen completo del día y confirma el cierre.', tip: 'Si hay diferencia, anótala y repórtala antes de confirmar. Una vez cerrada la caja no puedes editar el monto.' },
              ],
            },
            {
              title: 'Qué puede ver el Administrador y el CEO',
              intro: 'Además del proceso normal de apertura y cierre, el Administrador y el CEO pueden:',
              steps: [
                { text: 'Ver el historial de todos los cierres de caja anteriores — fecha, cajero que los hizo, monto esperado, monto real y diferencia.' },
                { text: 'Identificar tendencias de faltantes o sobrantes por cajero o por fecha.' },
                { text: 'Exportar el reporte de arqueo para entregarlo al contador o archivarlo.', tip: 'Revisa los arqueos periódicamente. Un faltante frecuente puede ser señal de un error de proceso o un descuido.' },
              ],
            },
          ],
        },

        /* ─────────────────────────── OPERACIONES ─────────────────────────── */
        {
          id: 'admin-ventas', icon: 'bi-list-check', title: 'Administrar Ventas',
          description: 'Vista completa del historial de ventas de la empresa. Exclusiva para Administrador y CEO. Permite revisar cualquier venta, ver su detalle y, si hubo un error, anularla.',
          sections: [
            {
              title: 'Qué verás al entrar',
              intro: 'La pantalla muestra una tabla con todas las ventas registradas:',
              steps: [
                { text: 'Tabla de ventas: cada fila es una venta. Muestra el número de factura, la fecha, el cliente, el cajero que la hizo, el total y su estado (Válida o Anulada).' },
                { text: 'Filtros de búsqueda (parte superior): puedes filtrar por rango de fechas, nombre del cliente, número de factura o cajero.' },
                { text: 'Botón "Ver detalle" en cada fila: abre una ventana con todos los productos de esa venta, descuentos aplicados y método de pago.' },
                { text: 'Botón "Anular" en cada fila: aparece solo en ventas que aún están vigentes. Solo Administrador y CEO lo ven.', tip: 'Usa los filtros de fecha para limitar los resultados — si hay muchas ventas, la carga puede tardar unos segundos.' },
              ],
            },
            {
              title: 'Cómo buscar una venta',
              steps: [
                { text: 'Ve a "Operaciones" → "Administrar Ventas" en el menú lateral.' },
                { text: 'Usa los filtros según lo que recuerdes: si tienes el número de factura úsalo directamente; si no, filtra por fecha y cliente.' },
                { text: 'Haz clic en "Buscar" o presiona Enter. La tabla se actualizará con los resultados.' },
                { text: 'Haz clic en "Ver detalle" en la fila de la venta que buscas para ver todos sus datos.' },
              ],
            },
            {
              title: 'Cómo anular una venta',
              steps: [
                { text: 'Encuentra la venta usando los filtros. Asegúrate de que sea la correcta revisando el detalle primero.' },
                { text: 'Haz clic en el botón "Anular" que aparece a la derecha de esa venta. Esto abrirá el wizard de Devoluciones con la venta pre-cargada.' },
                { text: 'En el wizard, elige el tipo "Anulación", ingresa el motivo y confirma.', warning: 'Una vez anulada, la venta no puede reactivarse. El inventario se restaura automáticamente y el cajero original recibirá una notificación en su pantalla.' },
                { text: 'La venta cambia a estado "Anulada". Puedes descargar el comprobante de cancelación (PDF con sello CANCELADA) desde el tab "Anulaciones" en Devoluciones.' },
              ],
              note: 'Solo Administrador y CEO pueden anular ventas. El cajero no ve el botón de anulación, pero sí recibe una alerta en pantalla cuando una de sus ventas es anulada. Los botones de Devolución y Cancelar venta solo aparecen si el módulo de Devoluciones está habilitado en el plan de tu empresa.',
            },
            {
              title: 'Lo que puede hacer el Administrador y el CEO',
              steps: [
                { text: 'Ambos pueden ver el historial completo de ventas y reimprimir cualquier comprobante.' },
                { text: 'Ambos pueden anular ventas. El cajero no tiene esa opción.' },
                { text: 'El CEO puede filtrar por sucursal para ver las ventas de todos los puntos de venta al mismo tiempo.', tip: 'El Administrador ve solo las ventas de su propia sucursal.' },
                { text: 'Ambos pueden exportar el historial a PDF desde el botón de exportación en la parte superior.' },
              ],
            },
          ],
        },

        {
          id: 'compras', icon: 'bi-cart-plus-fill', title: 'Compras',
          description: 'Registra las compras que haces a tus proveedores. Cada vez que guardas una compra, el inventario de los productos se actualiza de forma automática con las cantidades recibidas.',
          sections: [
            {
              title: 'Qué verás al entrar',
              intro: 'El módulo tiene dos secciones accesibles desde el menú:',
              steps: [
                { text: '"Crear Compra" (o "Nueva Compra"): formulario donde registras una compra nueva. Encontrarás campos para el proveedor, la fecha, el número de factura del proveedor y los productos recibidos.' },
                { text: '"Administrar Compras": tabla con el historial de todas las compras registradas, con filtros por fecha y proveedor.' },
                { text: 'En el formulario de nueva compra, el área de productos tiene un buscador propio donde seleccionas cada artículo recibido e ingresas la cantidad y el costo unitario.' },
                { text: 'Al final del formulario encontrarás la opción de marcar la compra como "A crédito" si no pagaste de contado.', tip: 'Tener las compras registradas correctamente es fundamental para que los reportes financieros sean precisos.' },
              ],
            },
            {
              title: 'Cómo registrar una compra',
              steps: [
                { text: 'Ve a "Operaciones" → "Crear Compra" en el menú lateral.' },
                { text: 'Selecciona el proveedor del que compraste. Si no está en la lista, créalo primero desde el módulo de Proveedores.' },
                { text: 'Ingresa el número de factura que te dio el proveedor y la fecha en que recibiste la mercancía.' },
                { text: 'En la sección de productos, busca cada artículo recibido. Ingresa la cantidad exacta y el precio de compra por unidad.' },
                { text: 'Repite para cada producto de la compra.' },
                { text: 'Si la compra fue a crédito, activa esa opción e ingresa la fecha límite de pago.' },
                { text: 'Revisa el total y haz clic en "Guardar compra". El inventario se actualizará al instante.', tip: 'Guarda la factura física o escaneada del proveedor. Si hay discrepancias futuras, la necesitarás como respaldo.' },
              ],
            },
            {
              title: 'Cómo ver el historial de compras',
              steps: [
                { text: 'Ve a "Operaciones" → "Administrar Compras" en el menú lateral.' },
                { text: 'La tabla muestra todas las compras con fecha, proveedor, total y estado de pago.' },
                { text: 'Usa los filtros de fecha o proveedor para encontrar compras específicas.' },
                { text: 'Haz clic en el ícono de detalle de cualquier compra para ver qué productos se recibieron y a qué precio.' },
              ],
            },
            {
              title: 'Lo que puede hacer el Administrador y el CEO',
              steps: [
                { text: 'El Administrador (rol 1) puede registrar compras nuevas y ver el historial de su sucursal.' },
                { text: 'El CEO puede ver el historial de compras de todas las sucursales usando el filtro de sucursal.' },
                { text: 'Ambos pueden exportar el historial de compras a PDF desde el botón de exportación en la parte superior.' },
                { text: 'Ambos pueden registrar compras a crédito, que quedan automáticamente en Cuentas por Pagar.', tip: 'Las compras que marques como "a crédito" generan automáticamente una deuda en el módulo de Cuentas por Pagar.' },
              ],
            },
          ],
        },

        {
          id: 'produccion', icon: 'bi-fire', title: 'Producción',
          description: 'Gestiona las recetas de tus productos elaborados y registra las órdenes de producción. Cada orden descuenta automáticamente las materias primas del inventario conforme se fabrican las unidades. Disponible para Administrador y CEO.',
          sections: [
            {
              title: 'Las dos pestañas de la pantalla',
              intro: 'Al entrar a Producción verás dos tabs:',
              steps: [
                { text: 'Recetas — lista todas las fórmulas de producción creadas. Cada receta define qué materias primas se necesitan y en qué cantidad para fabricar una unidad del producto terminado.' },
                { text: 'Órdenes — registro de todas las corridas de producción. Cada orden indica cuántas unidades se fabricaron, con qué receta, el costo total y el estado (Pendiente o Completada).' },
              ],
            },
            {
              title: 'Cómo crear una receta',
              steps: [
                { text: 'Ve a "Operaciones" → "Producción" en el menú lateral y asegúrate de estar en el tab "Recetas".' },
                { text: 'Haz clic en "Nueva receta".' },
                { text: 'Escribe el nombre de la receta. Úsalo de forma que identifiques fácilmente qué producto terminado produce.' },
                { text: 'Selecciona el producto terminado que resulta de esta receta. Debe existir previamente en el catálogo con tipo "Producto terminado".' },
                { text: 'Agrega los ingredientes uno a uno: selecciona la materia prima y escribe la cantidad necesaria por unidad producida.', tip: 'Las materias primas son productos con tipo "Materia prima" en el catálogo. Si no aparecen en la lista, primero créalos como productos con ese tipo.' },
                { text: 'Repite para cada ingrediente de la receta.' },
                { text: 'Haz clic en "Guardar receta". La receta quedará disponible para crear órdenes de producción.' },
              ],
            },
            {
              title: 'Cómo registrar una orden de producción',
              steps: [
                { text: 'Ve al tab "Órdenes" en la pantalla de Producción.' },
                { text: 'Haz clic en "Nueva orden".' },
                { text: 'Selecciona la receta que vas a producir.' },
                { text: 'Ingresa la cantidad de unidades que vas a fabricar en esta corrida.' },
                { text: 'El sistema te muestra el resumen: qué materias primas se van a consumir y el costo estimado total.' },
                { text: 'Haz clic en "Crear orden". La orden queda en estado Pendiente y el inventario de materias primas se descuenta inmediatamente.', warning: 'El descuento de inventario ocurre al crear la orden, no al completarla. Asegúrate de tener suficiente stock antes de confirmar.' },
                { text: 'Cuando la producción física termine, busca la orden en la lista y haz clic en "Completar". El estado cambia a Completada y el producto terminado suma unidades en inventario.' },
              ],
            },
            {
              title: 'Cómo ver el detalle de una orden',
              steps: [
                { text: 'En el tab "Órdenes", haz clic en el ícono de ojo (Ver detalle) en cualquier orden de la lista.' },
                { text: 'Se abre un panel con la información completa: receta utilizada, cantidad producida, ingredientes consumidos, costo por unidad y costo total.' },
                { text: 'Puedes usar este detalle para analizar la rentabilidad de cada corrida de producción.', tip: 'El reporte "Rendimiento por Receta" en el módulo de Reportes agrupa toda esta información por receta para un análisis de período.' },
              ],
            },
            {
              title: 'Lo que puede hacer el Administrador y el CEO',
              steps: [
                { text: 'Ambos pueden crear y eliminar recetas, y crear órdenes de producción.' },
                { text: 'Ambos pueden marcar órdenes como completadas.' },
                { text: 'El CEO puede ver las órdenes de producción de todas las sucursales.' },
                { text: 'Los reportes de producción (Órdenes de Producción y Rendimiento por Receta) están disponibles desde el módulo de Reportes para análisis de períodos específicos.', tip: 'Mantén las recetas actualizadas si cambian los ingredientes o las cantidades. Las órdenes antiguas conservan el costo que tenían cuando se crearon.' },
              ],
            },
          ],
        },

        /* ─────────────────────────── CONTACTOS ─────────────────────────── */
        {
          id: 'clientes', icon: 'bi-people-fill', title: 'Clientes',
          description: 'Directorio de clientes con sus datos de contacto, RTN, tipo, descuento habitual e historial de compras. Administrador y CEO pueden crear, editar y eliminar; el Cajero puede consultar durante el cobro.',
          sections: [
            {
              title: 'Qué verás al entrar',
              intro: 'La pantalla muestra la lista completa de clientes con:',
              steps: [
                { text: 'Tabla de clientes: nombre, RTN, teléfono, correo. Si el cliente tiene 65 años o más, aparece un badge "3a Edad" junto a su nombre.' },
                { text: 'Badge de tipo de cliente: muestra si es Normal, Frecuente o VIP con colores diferenciados.' },
                { text: 'Barra de búsqueda superior: busca por nombre, RTN o teléfono en tiempo real.' },
                { text: 'Botón "Nuevo cliente": abre el formulario de registro. Aparece solo si tienes permiso de edición (Admin o CEO).', tip: 'Desde el detalle de un cliente puedes ver su historial completo de compras.' },
              ],
            },
            {
              title: 'Campos del formulario de cliente',
              intro: 'Al crear o editar un cliente, los campos disponibles son:',
              steps: [
                { text: 'Nombre completo — obligatorio. Aparece en las facturas.' },
                { text: 'Teléfono, correo y dirección — datos de contacto.' },
                { text: 'RTN (Registro Tributario Nacional) — obligatorio para emitir facturas fiscales a nombre del cliente.' },
                { text: 'Tipo de cliente — Normal, Frecuente o VIP. Controla el badge que aparece en la caja y puede ser considerado en promociones.' },
                { text: 'Descuento habitual (%) — porcentaje automático que se aplica a este cliente en cada venta. Si se le asigna un 10%, el sistema propone ese descuento automáticamente en la caja.', tip: 'Un descuento habitual del 0% significa que no hay descuento automático. Puedes cambiarlo en cualquier momento.' },
                { text: 'Fecha de nacimiento — opcional. Cuando el cliente tiene 65 años o más, el sistema le asigna automáticamente el badge "3a Edad" y aplica el descuento de tercera edad en la caja.' },
                { text: 'Número de identidad — opcional, para registros internos.' },
                { text: 'Límite de crédito (L.) — monto máximo que este cliente puede tener pendiente de pago. Si se supera este monto, el sistema bloquea la venta a crédito y requiere clave de administrador para aprobarla. Déjalo vacío si no quieres límite.' },
                { text: 'Días de crédito — plazo máximo en días para pagar una venta a crédito. Si el cliente tiene facturas vencidas más allá de este plazo, el sistema también bloquea nuevas ventas a crédito hasta que regularice.', tip: 'El límite de crédito y los días de crédito son independientes — ambos pueden bloquear. Un cliente puede tener saldo bajo su límite pero igualmente ser bloqueado si tiene facturas vencidas.' },
              ],
            },
            {
              title: 'Cómo agregar o editar un cliente',
              steps: [
                { text: 'Para crear: haz clic en "Nuevo cliente" (botón superior derecho). Completa los campos y guarda.' },
                { text: 'Para editar: busca al cliente en la lista, haz clic en el botón de editar (lápiz) en su fila o abre su detalle y luego clic en editar.' },
                { text: 'El cliente queda disponible de inmediato en la pantalla de ventas.', tip: 'También puedes crear clientes directamente desde la pantalla de ventas (F3) sin salir del proceso de cobro.' },
              ],
            },
            {
              title: 'Permisos por rol',
              steps: [
                { text: 'Administrador y CEO: pueden crear, editar y eliminar clientes. Los botones de acción solo aparecen para estos roles.' },
                { text: 'Cajero: no accede al módulo directamente, pero puede consultar y seleccionar clientes desde la pantalla de ventas (F3) durante el cobro.', tip: 'Si un cliente ya no compra, desactívalo en lugar de eliminarlo para conservar su historial de compras.' },
              ],
            },
          ],
        },

        {
          id: 'proveedores', icon: 'bi-truck', title: 'Proveedores',
          description: 'Directorio de las empresas o personas que te abastecen de productos. Son necesarios para registrar compras. Solo Administrador y CEO pueden gestionar proveedores.',
          sections: [
            {
              title: 'Qué verás al entrar',
              intro: 'La pantalla muestra la lista de proveedores con su información principal:',
              steps: [
                { text: 'Tabla de proveedores: nombre, RTN/NIT, teléfono, correo y saldo pendiente si hay compras a crédito sin pagar.' },
                { text: 'Botón "Nuevo proveedor" (parte superior derecha): abre el formulario de registro.' },
                { text: 'Desde el detalle de cada proveedor puedes ver el historial de compras que le has hecho y las facturas pendientes de pago.', tip: 'Agrega el correo del proveedor para poder contactarlo rápidamente cuando necesites hacer un pedido.' },
              ],
            },
            {
              title: 'Cómo agregar un proveedor',
              steps: [
                { text: 'Ve a "Contactos" → "Proveedores" en el menú lateral.' },
                { text: 'Haz clic en "Nuevo proveedor".' },
                { text: 'Ingresa el nombre de la empresa o persona, teléfono, correo electrónico y dirección.' },
                { text: 'Si manejas crédito con este proveedor, indica las condiciones de pago (días de crédito).' },
                { text: 'Haz clic en "Guardar". El proveedor estará disponible para seleccionarlo al registrar compras.' },
              ],
            },
            {
              title: 'Cómo editar o desactivar un proveedor',
              steps: [
                { text: 'Busca al proveedor en la lista.' },
                { text: 'Haz clic en el ícono de editar (lápiz) para modificar sus datos.' },
                { text: 'Si ya no trabajas con ese proveedor, puedes desactivarlo para que no aparezca en las opciones de nuevas compras.', warning: 'No elimines proveedores que tienen historial de compras. Desactívalos en su lugar para conservar los registros.' },
              ],
            },
            {
              title: 'Lo que puede hacer el Administrador y el CEO',
              steps: [
                { text: 'Ambos pueden crear, editar y eliminar proveedores.' },
                { text: 'El CEO puede ver los proveedores de todas las sucursales.' },
                { text: 'Ambos pueden ver el saldo pendiente de cada proveedor (cuánto se les debe) directamente en la tabla.' },
              ],
            },
          ],
        },

        /* ─────────────────────────── CATÁLOGO ─────────────────────────── */
        {
          id: 'productos', icon: 'bi-box-seam', title: 'Productos',
          description: 'El catálogo completo de tu empresa: código, nombre, precio unitario, precio mayorista, tipo, stock, impuesto, unidad de medida y categorías. Los permisos de crear, editar y eliminar se configuran por rol en el sistema.',
          sections: [
            {
              title: 'Qué verás al entrar',
              intro: 'Una tabla paginada con todos los productos activos del sistema:',
              steps: [
                { text: 'Columnas de la tabla: #, Código, Nombre, Stock, Precio Unitario, Tipo (si el plan incluye producción), Promoción Activa, Etiqueta y Acciones.' },
                { text: 'Barra de búsqueda: filtra por nombre o código en tiempo real mientras escribes.' },
                { text: 'Selector de sucursal (CEO): aparece solo cuando hay más de una sucursal; permite ver el catálogo de cada punto de venta.' },
                { text: 'Botón "Agregar Producto": abre el formulario de creación. Solo aparece si tu rol tiene permiso de crear productos.' },
                { text: 'Botón "Exportar PDF": descarga la tabla actual como PDF.' },
                { text: 'Botón "Promociones": acceso rápido al módulo de Promociones por Producto.' },
                { text: 'Paginación al pie de la tabla: navega entre páginas cuando hay muchos productos. Muestra cuántos registros estás viendo del total.', tip: 'Usa la búsqueda para filtrar primero y luego exporta — el PDF incluye solo los registros filtrados.' },
              ],
            },
            {
              title: 'La columna Stock y cómo editarla',
              intro: 'El stock se gestiona de forma especial — no se edita dentro del formulario principal del producto:',
              steps: [
                { text: 'En la columna "Stock" de la tabla, al lado de la cantidad verás un ícono de lápiz pequeño. Haz clic en él.' },
                { text: 'Se abre el modal de Stock. Aquí defines el Stock Mínimo y el Stock Máximo para ese producto en la sucursal seleccionada.' },
                { text: 'Si la empresa tiene varias sucursales, puedes cambiar la sucursal desde el selector dentro del modal para editar los mínimos y máximos de cada punto de venta por separado.' },
                { text: 'Haz clic en "Guardar cambios". Estos valores se usan para las alertas de inventario automáticas.', tip: 'El stock real (cuántas unidades hay actualmente) se actualiza solo con cada venta o compra registrada. El modal de Stock solo configura los umbrales de alerta, no la cantidad actual.' },
              ],
            },
            {
              title: 'Cómo crear un producto nuevo',
              intro: 'El formulario tiene dos columnas con los campos del producto:',
              steps: [
                { text: 'Código único: el identificador interno del producto. Es el que se escanea o escribe en la caja para buscarlo. Debe ser único en toda la empresa.', warning: 'Si ya existe otro producto con ese código, el sistema no dejará guardarlo. Usa un código distinto.' },
                { text: 'Nombre del producto: usa un nombre claro y reconocible para el cajero.' },
                { text: 'Descripción: texto adicional que describe el producto. Es opcional pero útil.' },
                { text: 'Tipo de producto (solo si el plan incluye producción): elige entre General, Materia Prima o Producto Terminado. Las Materias Primas no tienen precio de venta — son ingredientes para recetas. Los Productos Terminados se fabrican con esas materias primas.' },
                { text: 'Impuesto (no aplica a Materias Primas): elige si el producto está Exento, tiene ISV 15% o ISV 18%. El sistema calcula el impuesto automáticamente en cada venta.' },
                { text: 'Proveedor (no aplica a Productos Terminados): el proveedor de quien compras este artículo. Debe existir en el módulo de Proveedores.' },
                { text: 'Precio por Unidad: el precio de venta al público. No aplica para Materias Primas.' },
                { text: 'Precio por mayoreo (opcional): precio especial cuando el cliente compra en cantidad alta. Se activa automáticamente si el cliente lleva igual o más que la cantidad mínima configurada.' },
                { text: 'Cantidad para activar mayoreo (opcional): el mínimo de unidades que debe llevar el cliente para que el precio mayorista se aplique automáticamente en la caja.', tip: 'Si el precio mayorista está configurado, en la caja el sistema lo aplica solo cuando la cantidad supera el umbral. No necesitas hacer nada manual.' },
                { text: 'Unidad de medida: cómo se cuenta este producto (Unidad, Caja, Libra, Litro, etc.). Debe existir en el módulo de Unidad de Medida.' },
                { text: 'Unidad de compra (solo Materia Prima, opcional): si compras la materia prima en una unidad diferente a la que usas en recetas. Ejemplo: compras en Libras pero usas en Gramos.' },
                { text: 'Factor de conversión (solo Materia Prima, aparece si escribiste unidad de compra): cuántas unidades de producción equivalen a 1 unidad de compra. Ejemplo: 1 Libra = 3 plátanos → factor 3.', tip: 'El factor de conversión permite que al registrar una compra en Libras, el stock de la receta se actualice en la unidad correcta automáticamente.' },
                { text: 'Categorías: haz clic en "Asignar categorías" para vincular el producto a una o varias categorías. Las categorías ayudan a organizar el catálogo y son necesarias para las Promociones por Categoría.' },
                { text: 'Haz clic en "Agregar Producto" o "Guardar Cambios". El producto queda disponible de inmediato en la pantalla de ventas.' },
              ],
            },
            {
              title: 'La etiqueta de código de barras',
              steps: [
                { text: 'En la columna "Etiqueta" de cada producto hay un botón con ícono de código de barras (bi-upc-scan).' },
                { text: 'Al hacer clic, se abre una ventana emergente con la etiqueta del producto lista para imprimir: nombre del producto, código de barras en formato CODE128 y precio.' },
                { text: 'El código de barras impreso corresponde exactamente al Código del producto. Cuando el cajero lo escanea en la caja, el sistema lo reconoce y agrega el producto al carrito.', tip: 'Imprime etiquetas y pégalas en los productos o estantes. El lector de barras del cajero envía el código al campo de búsqueda de la caja automáticamente.' },
              ],
            },
            {
              title: 'Cómo actualizar el precio de un producto',
              steps: [
                { text: 'Busca el producto en la lista.' },
                { text: 'Haz clic en el ícono de editar (lápiz amarillo) en la columna Acciones.' },
                { text: 'Cambia el Precio por Unidad y/o el Precio por mayoreo según necesites.' },
                { text: 'Haz clic en "Guardar Cambios". El nuevo precio aplica de inmediato en la siguiente venta.', tip: 'Las ventas ya registradas conservan el precio que tenían al momento de la venta. El cambio no afecta historial.' },
              ],
            },
            {
              title: 'Permisos por rol',
              steps: [
                { text: 'Los botones de Agregar, Editar y Eliminar aparecen únicamente si tu rol tiene habilitado ese permiso. Los permisos se configuran desde el módulo de Usuarios.' },
                { text: 'Si el botón "Agregar Producto" no aparece, tu rol no tiene permiso de creación. Pídele al CEO o Administrador que lo habilite o que cree el producto por ti.' },
                { text: 'El CEO puede ver y filtrar los productos de todas las sucursales usando el selector de sucursal que aparece en la parte superior.' },
                { text: 'Para agregar una promoción a un producto, usa el módulo "Promociones por Producto" del menú Catálogo.', tip: 'En lugar de eliminar un producto que ya no vendes, edítalo y desactívalo — así conservas su historial de ventas sin que aparezca en la caja.' },
              ],
            },
          ],
        },

        {
          id: 'categorias', icon: 'bi-tags-fill', title: 'Categorías',
          description: 'Las categorías agrupan tus productos en secciones lógicas (Bebidas, Snacks, Limpieza, etc.). Son necesarias para organizar el catálogo y también puedes hacerles promociones enteras desde el módulo "Promociones por Categoría". Solo Administrador y CEO las gestionan.',
          sections: [
            {
              title: 'Qué verás al entrar',
              intro: 'Una lista simple con todas las categorías creadas:',
              steps: [
                { text: 'Tabla de categorías: nombre y cantidad de productos que tiene asignados.' },
                { text: 'Botón "Nueva categoría" (parte superior derecha o "+"): abre el formulario de creación.' },
                { text: 'Cada categoría tiene botones para editar su nombre o eliminarla.', tip: 'No puedes eliminar una categoría que tiene productos asignados. Primero mueve esos productos a otra categoría.' },
              ],
            },
            {
              title: 'Cómo crear una categoría',
              steps: [
                { text: 'Ve a "Catálogo" → "Categorías" en el menú lateral.' },
                { text: 'Haz clic en "Nueva categoría".' },
                { text: 'Escribe el nombre. Puede tener letras, números y espacios. Ejemplos: "Bebidas Frías", "Snacks", "Artículos de Limpieza".' },
                { text: 'Haz clic en "Guardar". La categoría estará disponible de inmediato al crear o editar productos.' },
              ],
              note: 'No hay límite en la cantidad de categorías. Crea todas las que necesites para organizar bien tu catálogo.',
            },
            {
              title: 'Cómo eliminar una categoría',
              steps: [
                { text: 'Busca la categoría en la lista.' },
                { text: 'Verifica en la columna de productos que tenga 0 productos asignados.' },
                { text: 'Haz clic en el ícono de eliminar (basurero) y confirma.', warning: 'Si la categoría tiene productos, primero reasígnalos a otra categoría editando cada producto. No puedes eliminar categorías con productos activos.' },
              ],
            },
            {
              title: 'Lo que puede hacer el Administrador y el CEO',
              steps: [
                { text: 'Ambos pueden crear, editar y eliminar categorías.' },
                { text: 'Recuerda que desde el módulo "Promociones por Categoría" puedes aplicar descuentos automáticos a todos los productos de una categoría entera.', tip: 'Organiza bien tus categorías desde el principio. Cambiarlas después cuando hay muchos productos puede ser tedioso.' },
              ],
            },
          ],
        },

        /* ─────────────────────────── FINANZAS ─────────────────────────── */
        {
          id: 'cuentas-cobrar', icon: 'bi-credit-card-2-front', title: 'Cuentas por Cobrar',
          description: 'Muestra todas las ventas a crédito que los clientes aún no han pagado. Aquí registras los pagos cuando el cliente viene a saldar su deuda. Solo disponible para Administrador y CEO.',
          sections: [
            {
              title: 'Qué verás al entrar',
              intro: 'La pantalla muestra los saldos pendientes organizados por cliente:',
              steps: [
                { text: 'Lista de clientes con saldo: nombre del cliente, total de deuda y fecha del último movimiento.' },
                { text: 'Al hacer clic en un cliente, se despliegan sus facturas pendientes con el monto de cada una y la fecha de vencimiento.' },
                { text: 'Botón "Registrar pago" en cada factura: abre el formulario de pago.' },
                { text: 'Indicadores de color: rojo para facturas vencidas, amarillo para las que vencen pronto, verde para las que tienen plazo holgado.', tip: 'Revisa esta pantalla diariamente para saber qué clientes te deben y hacer seguimiento.' },
              ],
            },
            {
              title: 'Cómo registrar un pago de cliente',
              steps: [
                { text: 'Ve a "Finanzas" → "Cuentas por Cobrar" en el menú lateral.' },
                { text: 'Busca al cliente que está pagando. Puedes buscarlo por nombre en la barra de búsqueda.' },
                { text: 'Haz clic en el nombre del cliente para ver sus facturas pendientes.' },
                { text: 'Haz clic en "Registrar pago" en la factura que está pagando.' },
                { text: 'Ingresa el monto recibido y el método de pago (efectivo, transferencia, etc.).' },
                { text: 'Haz clic en "Confirmar". El saldo del cliente se reduce automáticamente.', tip: 'Puedes registrar pagos parciales. Si el cliente paga 500 de una deuda de 1,000, el sistema registra el abono y deja el saldo restante pendiente.' },
              ],
            },
            {
              title: 'Lo que puede hacer el Administrador y el CEO',
              steps: [
                { text: 'Ambos pueden ver los saldos pendientes, registrar pagos y ver el historial de pagos de cada cliente.' },
                { text: 'El CEO puede filtrar por sucursal para ver las cuentas por cobrar de todos los puntos de venta.' },
                { text: 'Ambos pueden exportar el reporte a PDF.', tip: 'Revisa primero los clientes con deudas vencidas — son los más urgentes de cobrar.' },
              ],
            },
          ],
        },

        {
          id: 'cuentas-pagar', icon: 'bi-credit-card', title: 'Cuentas por Pagar',
          description: 'Lista de las deudas que tienes con tus proveedores por compras realizadas a crédito. Aquí registras los pagos cuando saldes cada factura. Solo Administrador y CEO tienen acceso.',
          sections: [
            {
              title: 'Qué verás al entrar',
              intro: 'La pantalla organiza tus deudas por proveedor:',
              steps: [
                { text: 'Lista de proveedores con saldo pendiente: nombre del proveedor y monto total adeudado.' },
                { text: 'Al hacer clic en un proveedor, se muestran las facturas pendientes con el monto y la fecha de vencimiento de pago.' },
                { text: 'Botón "Pagar" en cada factura: abre el formulario para registrar el pago.' },
                { text: 'El sistema te muestra alertas cuando una factura está próxima a vencer.', tip: 'Pagar a tiempo a tus proveedores te ayuda a mantener buenos términos de crédito con ellos.' },
              ],
            },
            {
              title: 'Cómo registrar un pago a proveedor',
              steps: [
                { text: 'Ve a "Finanzas" → "Cuentas por Pagar" en el menú lateral.' },
                { text: 'Busca al proveedor al que le estás pagando.' },
                { text: 'Haz clic en el nombre del proveedor para ver sus facturas pendientes.' },
                { text: 'Haz clic en "Pagar" en la factura correspondiente.' },
                { text: 'Ingresa el monto pagado y el número de comprobante (número de transferencia u otra referencia de pago).' },
                { text: 'Haz clic en "Confirmar pago". La deuda se reduce y queda el registro del pago.', tip: 'Guarda el comprobante de pago. En caso de discrepancias con el proveedor, lo necesitarás como respaldo.' },
              ],
            },
            {
              title: 'Lo que puede hacer el Administrador y el CEO',
              steps: [
                { text: 'Ambos pueden ver las deudas pendientes con proveedores, registrar pagos y ver el historial.' },
                { text: 'El CEO puede filtrar por sucursal para ver todas las cuentas por pagar de la empresa.' },
                { text: 'Ambos pueden exportar el reporte a PDF.', tip: 'Paga primero las deudas más viejas y las de los proveedores más importantes para mantener buena relación comercial.' },
              ],
            },
          ],
        },

        {
          id: 'devoluciones', icon: 'bi-arrow-return-left', title: 'Devoluciones',
          description: 'Gestión completa de notas de crédito, anulaciones de ventas, retornos de productos y el historial de NCs aplicadas. Solo Administrador y CEO pueden procesar devoluciones.',
          sections: [
            {
              title: 'Las cuatro pestañas de la pantalla',
              intro: 'Al entrar a Devoluciones verás cuatro tabs:',
              steps: [
                { text: 'Devoluciones: lista de todas las Notas de Crédito (NC) generadas. Muestra número NC, fecha, factura original, cliente y monto. Desde aquí se procesan nuevas devoluciones.' },
                { text: 'Anulaciones: lista todas las ventas canceladas con su fecha de cancelación, total, método de pago y motivo. Incluye botón para descargar el PDF de cancelación de cada venta.' },
                { text: 'Retornos: registro de productos físicamente devueltos y pendientes de resolución (vuelve a stock o descarte).' },
                { text: 'Historial NC: muestra las Notas de Crédito que ya han sido aplicadas en ventas. Se pobla automáticamente cada vez que un cajero aplica una NC al momento del cobro.', tip: 'El tab "Historial NC" estará vacío al inicio — se llena con el uso diario.' },
              ],
            },
            {
              title: 'Cómo procesar una devolución (generar NC)',
              steps: [
                { text: 'Ve a "Finanzas" → "Devoluciones" en el menú lateral.' },
                { text: 'Estando en el tab "Devoluciones", haz clic en "Nueva Gestión".' },
                { text: 'En el paso 1 del wizard, elige el tipo: Devolución/Cambio o Anulación. Luego busca la factura por número.' },
                { text: 'El sistema muestra los productos de esa venta. Selecciona cuáles se devuelven y la cantidad por ítem.' },
                { text: 'Elige el motivo (producto dañado, equivocación del cliente, error en el pedido, etc.).' },
                { text: 'Revisa el resumen: monto del crédito que se generará.' },
                { text: 'Haz clic en "Confirmar". El sistema pedirá confirmación adicional.', warning: 'Esta acción restaura el inventario automáticamente. Verifica productos y cantidades antes de confirmar.' },
                { text: 'Se genera la Nota de Crédito (NC) disponible para que el cajero la aplique en la próxima venta del mismo cliente.' },
              ],
            },
            {
              title: 'Lo que puede hacer el Administrador y el CEO',
              steps: [
                { text: 'Ambos pueden generar NCs, ver el historial completo y descargar PDFs de cancelación.' },
                { text: 'Desde el tab Anulaciones pueden descargar el comprobante de cancelación de cualquier venta (incluye sello "CANCELADA" y motivo).' },
                { text: 'El CEO puede ver las devoluciones de todas las sucursales.' },
                { text: 'Si hay muchas devoluciones del mismo producto, puede ser señal de un problema de calidad. Revisa el historial periódicamente.', tip: 'El cajero no puede generar NCs — pero sí puede aplicarlas al momento del cobro (ver módulo Notas de Crédito).' },
              ],
            },
          ],
        },

        {
          id: 'notas-credito', icon: 'bi-receipt-cutoff', title: 'Notas de Crédito',
          description: 'Una Nota de Crédito (NC) es un saldo a favor del cliente generado después de una devolución. El cajero puede aplicar una o varias NCs disponibles al momento de cobrar una nueva venta, reduciendo el total a pagar.',
          sections: [
            {
              title: 'Cómo funciona una Nota de Crédito',
              intro: 'El flujo completo de una NC tiene dos partes:',
              steps: [
                { text: 'El Administrador o CEO genera la NC en el módulo Devoluciones. La NC queda registrada a nombre del cliente con un saldo disponible (ej: L. 172.50).' },
                { text: 'La próxima vez que ese cliente compra, el cajero puede aplicar la NC para reducir el total.' },
                { text: 'El sistema soporta múltiples NCs en una misma venta — todas las NCs disponibles del cliente se acumulan.' },
                { text: 'Si la NC cubre el 100% del total, el cobro queda en L. 0.00 y puede procesarse sin ingresar dinero.', tip: 'Las NCs son por empresa: solo aplican en ventas de la misma sucursal donde se generaron.' },
              ],
            },
            {
              title: 'Cómo aplicar una NC en una venta (cajero)',
              steps: [
                { text: 'Agrega los productos al carrito normalmente.' },
                { text: 'Haz clic en el botón "Aplicar NC" del footer (o presiona la tecla correspondiente si está disponible).' },
                { text: 'Se abre el modal de NCs. Verás la lista de todas las NCs disponibles para el cliente actual con su saldo restante.' },
                { text: 'Selecciona una o varias NCs haciendo clic en cada una. Las seleccionadas se resaltan en dorado.' },
                { text: 'Haz clic en "Aplicar NCs seleccionadas". El total del carrito se reduce automáticamente.', warning: 'Verifica que el cliente correcto esté seleccionado antes de aplicar la NC. Las NCs son intransferibles.' },
                { text: 'Abre el modal de pago (Registrar Pago). El total ya refleja el descuento de la NC.' },
                { text: 'Si la NC cubre todo, el total será L. 0.00 y puedes confirmar el pago directamente.' },
              ],
            },
            {
              title: 'Lo que ve el Administrador y el CEO',
              steps: [
                { text: 'En "Finanzas" → "Devoluciones" → tab "Historial NC" pueden ver todas las NCs que ya se han aplicado en ventas.' },
                { text: 'Cada registro muestra: número de NC, monto aplicado, factura donde se usó y fecha.' },
                { text: 'Las NCs pendientes (no usadas aún) aparecen en el tab "Devoluciones" con su saldo disponible.', tip: 'Si una NC se aplica parcialmente (el saldo es mayor que el total de la venta), el sistema aplica solo lo que cubre el total. El saldo restante permanece disponible.' },
              ],
            },
          ],
        },

        /* ─────────────────────────── REPORTES ─────────────────────────── */
        {
          id: 'reportes', icon: 'bi-graph-up', title: 'Reportes',
          description: 'Estadísticas y resúmenes de tu negocio: ventas por período, productos más vendidos, ingresos por método de pago, rendimiento por cajero, producción y más. Exclusivo para Administrador y CEO.',
          sections: [
            {
              title: 'Qué verás al entrar',
              intro: 'La pantalla de reportes tiene varias secciones o pestañas:',
              steps: [
                { text: 'Selector de tipo de reporte: ventas por día, por producto, por cajero, por método de pago, reportes de producción y otros disponibles.' },
                { text: 'Selector de período: define la fecha de inicio y la fecha de fin del reporte.' },
                { text: 'Botón "Generar reporte": construye el reporte con los filtros seleccionados.' },
                { text: 'Área de resultados: muestra tablas y gráficos con los datos.' },
                { text: 'Botones de exportación: descarga el reporte en Excel o PDF.', tip: 'Los reportes se generan en tiempo real con los datos actuales del sistema.' },
              ],
            },
            {
              title: 'Cómo generar un reporte de ventas',
              steps: [
                { text: 'Ve a "Reportes" → "Reportes" en el menú lateral.' },
                { text: 'Selecciona el tipo de reporte. Para ventas del período, elige "Reporte de ventas".' },
                { text: 'Define el rango de fechas: fecha de inicio y fecha de fin.' },
                { text: 'Haz clic en "Generar reporte". En segundos verás los totales, la lista de ventas y los gráficos.' },
                { text: 'Si necesitas compartirlo, haz clic en "Exportar a Excel" o "Exportar a PDF".', tip: 'Genera reportes semanales para identificar qué días venden más, qué productos rotan rápido y en qué horarios hay más actividad.' },
              ],
            },
            {
              title: 'Tipos de reporte de producción',
              intro: 'El módulo de reportes incluye dos reportes específicos para producción:',
              steps: [
                { text: 'Órdenes de Producción — lista todas las órdenes del período seleccionado con receta, producto, estado, costo total y costo por unidad. Puedes filtrar por rango de fechas y limitar el número de resultados.' },
                { text: 'Rendimiento por Receta — agrupa las órdenes completadas por receta y calcula el costo promedio por unidad y el margen estimado comparado con el precio de venta. Útil para identificar qué productos son más rentables de fabricar.', tip: 'El margen mostrado en "Rendimiento por Receta" usa el precio de venta actual del producto. Si el precio cambió durante el período, el margen puede no reflejar el histórico exacto.' },
              ],
            },
            {
              title: 'Lo que puede hacer el Administrador y el CEO',
              steps: [
                { text: 'El Administrador puede generar reportes de su propia sucursal en todos los tipos disponibles.' },
                { text: 'El CEO puede generar reportes de cualquier sucursal o de toda la empresa, filtrando por sucursal.' },
                { text: 'Ambos pueden personalizar el encabezado del PDF (logo y datos de la empresa) con el botón de configurar encabezado.' },
                { text: 'Ambos pueden exportar en PDF y en Excel.', tip: 'El Excel es útil para análisis con fórmulas propias. El PDF es mejor para presentaciones o archivar.' },
              ],
            },
          ],
        },

        {
          id: 'reporte-anulaciones', icon: 'bi-x-circle', title: 'Reporte de Anulaciones',
          description: 'Resumen estadístico y tabla detallada de todas las ventas que han sido anuladas. Permite filtrar por rango de fechas para analizar patrones de cancelación. Accesible para Administrador y CEO.',
          sections: [
            {
              title: 'Qué verás al entrar',
              intro: 'La pantalla muestra estadísticas de anulaciones y una tabla filtrable:',
              steps: [
                { text: 'Tarjetas de resumen: total de anulaciones en el período, monto total anulado y distribución por método de pago.' },
                { text: 'Filtros de fecha: selector de fecha inicio y fecha fin para acotar el análisis.' },
                { text: 'Botón "Filtrar": aplica el rango de fechas y recarga la tabla.' },
                { text: 'Tabla de anulaciones: Factura · Fecha anulación · Total · Método de pago · Cajero · Motivo.', tip: 'Si el período cargado al entrar no muestra datos, ajusta el rango de fechas — el filtro por defecto puede ser del día actual.' },
              ],
            },
            {
              title: 'Cómo generar el reporte de anulaciones',
              steps: [
                { text: 'Ve a "Reportes" → "Reporte Anulaciones" en el menú lateral.' },
                { text: 'Define el rango de fechas: escribe la fecha de inicio y la fecha de fin.' },
                { text: 'Haz clic en "Filtrar". La tabla se actualizará con las anulaciones del período.' },
                { text: 'Revisa la columna "Motivo" para identificar las razones más frecuentes de cancelación.', tip: 'Un patrón de anulaciones frecuentes del mismo cajero puede indicar errores de entrada de datos. Revisa con el empleado para corregir el hábito.' },
              ],
            },
            {
              title: 'Lo que puede hacer el Administrador y el CEO',
              steps: [
                { text: 'El Administrador ve las anulaciones de su propia sucursal.' },
                { text: 'El CEO puede ver las anulaciones de todas las sucursales.' },
                { text: 'Ambos pueden usar el reporte para auditorías internas y para detectar patrones inusuales.', tip: 'Las anulaciones también se listan en "Finanzas" → "Devoluciones" → tab "Anulaciones", donde además puedes descargar el PDF de cada una.' },
              ],
            },
          ],
        },

        {
          id: 'nomina', icon: 'bi-wallet2', title: 'Planilla',
          description: 'Módulo para calcular y registrar los salarios, deducciones y bonificaciones de los empleados por cada período de pago. Solo Administrador y CEO tienen acceso.',
          sections: [
            {
              title: 'Qué verás al entrar',
              intro: 'La pantalla muestra los empleados activos y sus datos de pago:',
              steps: [
                { text: 'Lista de empleados activos: nombre, cargo, salario base y período de pago (quincenal/mensual).' },
                { text: 'Selector de período: elige las fechas del período que vas a calcular.' },
                { text: 'Botón "Generar planilla": calcula los totales de todos los empleados para ese período.' },
                { text: 'Una vez generada, puedes ver el detalle de cada empleado: salario bruto, deducciones y pago neto.', tip: 'Asegúrate de que los datos de salario de cada empleado estén correctos en el módulo de Usuarios antes de generar la planilla.' },
              ],
            },
            {
              title: 'Cómo generar la planilla',
              steps: [
                { text: 'Ve a "Reportes" → "Planilla" en el menú lateral.' },
                { text: 'Selecciona el período: quincenal o mensual, y define las fechas exactas.' },
                { text: 'El sistema carga todos los empleados activos con su salario base.' },
                { text: 'Si hay bonos o deducciones especiales para este período (ausencias, horas extra, comisiones), agrégalos en los campos correspondientes de cada empleado.' },
                { text: 'Haz clic en "Generar planilla". Verás el resumen con totales brutos y netos.' },
                { text: 'Exporta a Excel para compartirla con tu contador o para el archivo de nómina.', tip: 'Guarda una copia de cada planilla generada. Son documentos que pueden requerirse en auditorías laborales.' },
              ],
            },
            {
              title: 'Lo que puede hacer el Administrador y el CEO',
              steps: [
                { text: 'El Administrador puede generar y ver la planilla de su sucursal, marcar pagos como realizados y eliminar registros.' },
                { text: 'El CEO puede ver y gestionar las planillas de todas las sucursales.' },
                { text: 'Ambos pueden editar manualmente los montos de cada empleado antes de generar — para agregar bonos o deducciones especiales de ese período.' },
              ],
            },
          ],
        },

        {
          id: 'libros', icon: 'bi-journal-bookmark', title: 'Libros Fiscales',
          description: 'Registros oficiales de ventas y compras requeridos por el SAR para la declaración de impuestos. El sistema los genera automáticamente a partir de las ventas y compras registradas. Solo Administrador y CEO tienen acceso.',
          sections: [
            {
              title: 'Qué verás al entrar',
              intro: 'La pantalla tiene dos libros principales:',
              steps: [
                { text: 'Libro de Ventas: todas las ventas del período seleccionado con número de factura, cliente, RTN, total gravado, ISV y total.' },
                { text: 'Libro de Compras: todas las compras del período con proveedor, número de factura, total y crédito fiscal.' },
                { text: 'Selector de período (mes y año): filtra los registros del mes que necesitas consultar.' },
                { text: 'Botones de exportación: descarga en el formato requerido por el SAR o para entregar al contador.', tip: 'Revisa los libros cada mes antes del cierre contable para detectar facturas faltantes o errores.' },
              ],
            },
            {
              title: 'Cómo consultar los libros fiscales',
              steps: [
                { text: 'Ve a "Reportes" → "Libros Fiscales" en el menú lateral.' },
                { text: 'Selecciona el mes y el año que deseas consultar.' },
                { text: 'Verás el Libro de Ventas y el Libro de Compras con todos los registros de ese período.' },
                { text: 'Revisa que los totales cuadren con tus registros físicos.' },
                { text: 'Haz clic en "Exportar" para descargarlos en el formato adecuado y entregárselos a tu contador o al SAR.', tip: 'Exporta los libros a más tardar los primeros días del mes siguiente para mantener la contabilidad al día.' },
              ],
            },
            {
              title: 'Lo que puede hacer el Administrador y el CEO',
              steps: [
                { text: 'Ambos pueden ver y exportar los libros fiscales.' },
                { text: 'El CEO puede filtrar por sucursal para ver los libros de toda la empresa.' },
                { text: 'Hay tres botones de exportación: PDF del Libro de Ventas, PDF del Libro de Compras y Excel con ambos libros juntos.', warning: 'Los libros fiscales son documentos legales. Si detectas un error en una factura, corrígelo en el módulo de Ventas o Compras — los libros se actualizan solos. No modifiques los datos directamente.' },
              ],
            },
          ],
        },

        /* ─────────────────────────── SISTEMA ─────────────────────────── */
        {
          id: 'empleados', icon: 'bi-person-fill', title: 'Usuarios',
          description: 'Administra los accesos al sistema. Aquí creas cuentas nuevas, asignas el rol de cada persona y puedes desactivar accesos cuando alguien deja de trabajar en la empresa. Solo Administrador y CEO.',
          sections: [
            {
              title: 'Los roles del sistema y qué puede hacer cada uno',
              intro: 'Hay dos roles que podés asignar desde esta pantalla:',
              steps: [
                { text: 'Cajero — acceso a Ventas, Cotizaciones y Arqueo de Caja. Es el rol para empleados de mostrador que solo necesitan hacer cobros.' },
                { text: 'Administrador — acceso completo dentro de su sucursal. Puede hacer ventas, compras, ver reportes, administrar usuarios, clientes, proveedores y configurar el sistema. Puede crear Cajeros y más Administradores para su propia sucursal.', tip: 'Asigna siempre el rol mínimo necesario. Un cajero no necesita acceso a reportes financieros ni a la gestión de usuarios.' },
              ],
            },
            {
              title: 'Qué verás al entrar',
              intro: 'La pantalla muestra todos los usuarios del sistema:',
              steps: [
                { text: 'Tabla de usuarios: nombre, apellido, nombre de usuario, rol y estado (Activo/Inactivo).' },
                { text: 'Botón "Nuevo usuario" (parte superior derecha o "+"): abre el formulario de registro.' },
                { text: 'Cada usuario tiene botones para editar su información, cambiar su rol o desactivar su acceso.' },
                { text: 'Barra de búsqueda: filtra por nombre o usuario de acceso.', tip: 'No puedes ver las contraseñas de otros usuarios — solo puedes restablecerlas si el empleado las olvida.' },
              ],
            },
            {
              title: 'Cómo crear un usuario nuevo',
              steps: [
                { text: 'Ve a "Sistema" → "Usuarios" en el menú lateral.' },
                { text: 'Haz clic en "Nuevo usuario".' },
                { text: 'Ingresa el nombre y apellido del empleado.' },
                { text: 'Crea el nombre de usuario con el que iniciará sesión. Usa algo fácil de recordar, como su nombre sin espacios o sus iniciales más un número.' },
                { text: 'Crea una contraseña temporal segura (mezcla letras mayúsculas, minúsculas y números).' },
                { text: 'Selecciona el rol: Cajero o Administrador.' },
                { text: 'Selecciona la sucursal a la que pertenecerá el usuario.' },
                { text: 'Haz clic en "Guardar". El empleado ya puede iniciar sesión.', tip: 'Entrégale el usuario y la contraseña en persona y pídele que la cambie en su primer inicio de sesión.' },
              ],
            },
            {
              title: 'Cómo desactivar el acceso de un empleado',
              steps: [
                { text: 'Busca al empleado en la lista.' },
                { text: 'Haz clic en el botón "Desactivar" en su fila.' },
                { text: 'Confirma la acción. El empleado no podrá iniciar sesión desde ese momento.', warning: 'No puedes desactivar tu propio usuario mientras estás conectado con él. Pídele a otro administrador que lo haga.' },
                { text: 'El historial de ventas y acciones del empleado se conserva — solo pierde el acceso para entrar.' },
              ],
            },
          ],
        },

        {
          id: 'configuracion', icon: 'bi-gear-fill', title: 'Configuración',
          description: 'Ajusta los datos de tu empresa, configura el sistema SAR para facturación y gestiona la seguridad de tu cuenta. El Administrador y el CEO tienen acceso completo; cualquier usuario puede cambiar su propia contraseña. Para gestionar la suscripción y plan de POSWAVE, usa el módulo "Plan & Facturación".',
          sections: [
            {
              title: 'Qué encontrarás en configuración',
              intro: 'El módulo de configuración tiene varias secciones:',
              steps: [
                { text: '"Mi cuenta" (accesible desde el ícono de usuario arriba a la derecha): aquí cualquier usuario puede cambiar su contraseña y ver su información personal.' },
                { text: '"Datos de la empresa" (solo Administrador y CEO): nombre, dirección, teléfono, correo y logo de la empresa. Esta información aparece en todos los recibos y facturas.' },
                { text: '"Configuración SAR" (solo Administrador): datos de facturación fiscal como CAI, rango de facturas autorizadas y fecha de vencimiento del CAI.' },
                { text: '"Sucursales" (solo CEO): gestión de múltiples puntos de venta si la empresa tiene más de una sucursal.', tip: 'Mantén los datos de la empresa siempre actualizados — aparecen en cada factura que le entregas a tus clientes.' },
              ],
            },
            {
              title: 'Cómo actualizar los datos de la empresa',
              steps: [
                { text: 'Ve a "Sistema" → "Configuración" en el menú lateral, o haz clic en tu nombre arriba a la derecha y selecciona "Mi cuenta".' },
                { text: 'Busca la sección "Datos de la empresa".' },
                { text: 'Actualiza el nombre, dirección, teléfono, correo electrónico y cualquier otro dato necesario.' },
                { text: 'Haz clic en "Guardar". Los cambios se reflejarán de inmediato en los nuevos recibos.', tip: 'Si cambias la dirección o el nombre de la empresa, verifica también el logo y la información de contacto.' },
              ],
            },
            {
              title: 'Cómo cambiar tu contraseña',
              steps: [
                { text: 'Haz clic en tu nombre en la parte superior derecha de la pantalla y selecciona "Mi cuenta".' },
                { text: 'Busca la sección "Cambiar contraseña".' },
                { text: 'Escribe tu contraseña actual (para confirmar que eres tú).' },
                { text: 'Escribe la nueva contraseña dos veces.' },
                { text: 'Haz clic en "Guardar".', warning: 'Usa una contraseña difícil de adivinar: combina letras mayúsculas, minúsculas, números y símbolos. No la compartas con nadie, ni siquiera con el administrador.' },
              ],
            },
            {
              title: 'Cómo cambiar entre modo claro y oscuro',
              steps: [
                { text: 'En el menú lateral izquierdo, al fondo, encontrarás un ícono de sol (modo claro) o luna (modo oscuro).' },
                { text: 'Haz clic en él para cambiar el tema. El cambio es inmediato y se guarda para la próxima vez que entres.', tip: 'El modo oscuro es más cómodo en ambientes con poca luz o para turnos nocturnos.' },
              ],
            },
          ],
        },

        /* ──────────────── MÓDULOS ADICIONALES DE CATÁLOGO ──────────────── */
        {
          id: 'unidad-medida', icon: 'bi-rulers', title: 'Unidad de Medida',
          description: 'Define cómo se miden o cuentan los productos en tu inventario: unidades, cajas, libras, litros, etc. Cada producto debe tener asignada una unidad de medida para que el sistema sepa cómo contarlo cuando entra o sale del inventario.',
          sections: [
            {
              title: 'Para qué sirve una unidad de medida',
              intro: 'La unidad de medida le dice al sistema cómo está contando ese producto:',
              steps: [
                { text: 'Un refresco se puede vender por "Unidad" — cada botella es 1 unidad.' },
                { text: 'Un aceite se puede vender por "Litro" o por "Galón".' },
                { text: 'Un producto a granel como carne o queso se puede vender por "Libra" o "Kilogramo".' },
                { text: 'Un detergente puede venderse por "Caja" si viene empacado así.', tip: 'Si no usas unidades de medida especiales, deja todos los productos en "Unidad" — es lo más común para tiendas pequeñas.' },
              ],
            },
            {
              title: 'Qué ves cuando entras',
              intro: 'Una tabla sencilla con todas las unidades de medida creadas:',
              steps: [
                { text: 'Tabla de unidades: número, nombre de la unidad (Ej: Unidad, Caja, Libra, Litro) y cuántos productos la están usando.' },
                { text: 'Botón "Ver productos" en cada fila: muestra qué productos tienen asignada esa unidad.' },
                { text: 'Barra de búsqueda en la parte superior: filtra por nombre de unidad.' },
                { text: 'Botón "Nueva unidad de medida": abre el formulario de creación.' },
                { text: 'Botones de editar y eliminar en cada fila (solo CEO).', tip: 'No puedes eliminar una unidad que tiene productos asignados. Primero cambia la unidad de esos productos.' },
              ],
            },
            {
              title: 'Cómo crear una unidad de medida',
              steps: [
                { text: 'Ve a "Catálogo" → "Unidad de Medida" en el menú lateral.' },
                { text: 'Haz clic en "Nueva unidad de medida".' },
                { text: 'Escribe el nombre de la unidad. Ejemplos: Unidad, Caja, Paquete, Libra, Kilo, Litro, Galón, Docena.' },
                { text: 'Haz clic en "Guardar". La unidad ya estará disponible para asignarla a productos desde el módulo de Productos.', tip: 'Crea solo las unidades que realmente necesitas. Tener demasiadas puede causar confusión al momento de registrar productos.' },
              ],
            },
            {
              title: 'Lo que puede hacer el Administrador y el CEO',
              steps: [
                { text: 'El Administrador puede crear y editar unidades de medida.' },
                { text: 'Solo el CEO puede eliminarlas.' },
                { text: 'El CEO también puede ver a qué productos está asignada cada unidad antes de eliminarla.' },
              ],
            },
          ],
        },

        {
          id: 'promociones-producto', icon: 'bi-tag-fill', title: 'Promociones por Producto',
          description: 'Crea descuentos automáticos para productos específicos. Una vez activada la promoción, el descuento se aplica solo durante el período que definiste — sin que el cajero tenga que hacer nada.',
          sections: [
            {
              title: 'Cómo funcionan las promociones de productos',
              intro: 'Una promoción de producto es un descuento programado que el sistema aplica automáticamente:',
              steps: [
                { text: 'Defines qué producto tiene la promoción, cuánto descuento (en porcentaje), y desde qué fecha hasta cuándo.' },
                { text: 'También puedes definir en qué horario del día aplica: por ejemplo, solo de 2pm a 6pm.' },
                { text: 'Cuando llega la fecha y la hora configurada, el sistema activa el descuento solo. El precio en la pantalla de ventas ya aparecerá con el descuento aplicado.' },
                { text: 'Cuando termina el período o el horario, el precio vuelve al normal automáticamente.', tip: 'El cajero no necesita saber ni hacer nada — el descuento se aplica solo si está dentro del período y horario configurado.' },
              ],
            },
            {
              title: 'Qué ves cuando entras',
              intro: 'La pantalla tiene el formulario para crear una promoción y la lista de promociones activas:',
              steps: [
                { text: 'Lista de promociones: nombre de la promoción, producto afectado, porcentaje de descuento, fecha de inicio, fecha de fin y horario de activación.' },
                { text: 'Formulario de nueva promoción en la parte superior (o en un panel lateral).', tip: 'Si un producto ya tiene una promoción activa y le creas otra, puede haber conflicto. Termina la primera antes de crear la segunda.' },
              ],
            },
            {
              title: 'Cómo crear una promoción para un producto',
              steps: [
                { text: 'Ve a "Catálogo" → "Promociones Producto" en el menú lateral.' },
                { text: 'En el campo "Producto", escribe el nombre del producto al que le quieres poner la promoción y selecciónalo de la lista.' },
                { text: 'Escribe un nombre para la promoción. Ejemplos: "Oferta de verano", "2x1 jugos", "Descuento de lanzamiento".' },
                { text: 'Ingresa el porcentaje de descuento. Ejemplo: si quieres dar un 20% de descuento, escribe 20.' },
                { text: 'Selecciona la fecha de inicio y la fecha final de la promoción.' },
                { text: 'Si la promoción aplica solo en cierto horario (por ejemplo, happy hour de 3pm a 6pm), configura la hora de inicio y la hora de fin.' },
                { text: 'Haz clic en "Guardar". La promoción queda activa automáticamente cuando llegue la fecha y hora configurada.', warning: 'Verifica bien el porcentaje y las fechas antes de guardar. Un descuento muy alto puede afectar tus ganancias sin darte cuenta.' },
              ],
            },
            {
              title: 'Lo que puede hacer el Administrador y el CEO',
              steps: [
                { text: 'Ambos pueden crear, editar y eliminar promociones por producto.' },
                { text: 'El CEO puede ver y gestionar las promociones de todas las sucursales.' },
                { text: 'Ambos pueden ver qué promociones están activas en este momento y cuáles vienen próximamente.', tip: 'Usa las promociones para rotar productos que tienen mucho stock o para incentivar ventas en días de baja actividad.' },
              ],
            },
          ],
        },

        {
          id: 'promociones-categorias', icon: 'bi-tags', title: 'Promociones por Categoría',
          description: 'Igual que las promociones por producto, pero se aplican a todos los productos de una categoría entera de una sola vez. Útil para hacer ofertas generales de un tipo de producto.',
          sections: [
            {
              title: 'Cuándo usar promoción por categoría vs por producto',
              steps: [
                { text: 'Usa "Promoción por Producto" cuando solo quieres hacer descuento en uno o pocos artículos específicos. Ejemplo: solo al jugo de naranja de 1L.' },
                { text: 'Usa "Promoción por Categoría" cuando el descuento aplica a todo un grupo. Ejemplo: todos los snacks con 15% de descuento el fin de semana.', tip: 'La promoción por categoría es más eficiente — en lugar de crear una promoción por cada producto, creas una sola y se aplica a todos automáticamente.' },
              ],
            },
            {
              title: 'Cómo crear una promoción por categoría',
              steps: [
                { text: 'Ve a "Catálogo" → "Promociones Categorías" en el menú lateral.' },
                { text: 'En el campo "Categoría", escribe el nombre de la categoría y selecciónala de la lista.' },
                { text: 'Escribe el nombre de la promoción.' },
                { text: 'Ingresa el porcentaje de descuento.' },
                { text: 'Define la fecha de inicio y la fecha de fin.' },
                { text: 'Si aplica solo en cierto horario del día, configura la hora de inicio y la hora de fin.' },
                { text: 'Haz clic en "Guardar". Todos los productos de esa categoría tendrán el descuento activo automáticamente.', warning: 'Si un producto de la categoría ya tenía una promoción individual, pueden aplicarse dos descuentos al mismo tiempo. Verifica cuáles están activas antes de crear una nueva.' },
              ],
            },
            {
              title: 'Lo que puede hacer el Administrador y el CEO',
              steps: [
                { text: 'Ambos pueden crear, editar y eliminar promociones por categoría.' },
                { text: 'El CEO puede gestionar las promociones de todas las sucursales.' },
                { text: 'Ambos pueden ver el listado de promociones activas y las que vienen próximamente.', tip: 'Combina promociones de categoría con días específicos de la semana para crear estrategias de ventas — por ejemplo, descuento en bebidas los viernes.' },
              ],
            },
          ],
        },

        {
          id: 'alertas-inventario', icon: 'bi-exclamation-triangle-fill', title: 'Alertas de Inventario',
          description: 'Muestra en tiempo real qué productos están quedándose sin existencias. El sistema los ordena por nivel de urgencia para que sepas qué comprar primero.',
          sections: [
            {
              title: 'Cómo funciona el sistema de alertas',
              intro: 'El sistema evalúa cada producto y le asigna un nivel de urgencia:',
              steps: [
                { text: 'URGENTE (rojo) — el producto está prácticamente agotado o tiene existencias muy bajas. Hay que comprar ya.' },
                { text: 'ATENCIÓN (amarillo) — el producto tiene pocas existencias. Pronto se va a acabar si no se repone.' },
                { text: 'NORMAL (verde) — el producto tiene existencias suficientes por el momento.', tip: 'El sistema actualiza las alertas en tiempo real con cada venta que se registra. No necesitas recargar la página.' },
              ],
            },
            {
              title: 'Qué ves cuando entras',
              intro: 'Una vista de tarjetas con los productos que necesitan atención:',
              steps: [
                { text: 'Lista de productos ordenada por urgencia: los más críticos aparecen primero.' },
                { text: 'Cada tarjeta muestra el nombre del producto, su descripción y su nivel de urgencia (URGENTE, ATENCIÓN o NORMAL).' },
                { text: 'Haz clic en cualquier tarjeta para ver el detalle completo: existencias actuales, último movimiento de inventario y el estado exacto.' },
                { text: 'Si no hay ninguna alerta activa, la pantalla muestra un mensaje indicando que el inventario está en orden.', tip: 'Revisa esta pantalla al inicio de cada día de trabajo para anticiparte a los productos que van a hacer falta.' },
              ],
            },
            {
              title: 'Cómo usar las alertas para planificar compras',
              steps: [
                { text: 'Ve a "Reportes" → "Alertas de Inventario" en el menú lateral.' },
                { text: 'Revisa los productos en estado URGENTE — esos son los primeros que debes comprar.' },
                { text: 'Luego revisa los de ATENCIÓN para incluirlos en el siguiente pedido antes de que se agoten.' },
                { text: 'Desde cada tarjeta puedes ver el nombre exacto del producto para buscarlos en el módulo de Compras o contactar al proveedor.', tip: 'Configura el mínimo de existencias de cada producto en el módulo de Productos. El sistema usa ese valor para calcular cuándo activar la alerta.' },
              ],
            },
            {
              title: 'Lo que puede hacer el Administrador y el CEO',
              steps: [
                { text: 'Ambos pueden ver las alertas de inventario de su sucursal.' },
                { text: 'El CEO puede ver las alertas de todas las sucursales para coordinar reabastecimiento centralizado.' },
                { text: 'Las alertas se actualizan automáticamente — no hay que hacer nada para que funcionen.', tip: 'Si un producto siempre está en alerta pero no lo vendes mucho, revisa si el mínimo de existencias configurado es demasiado alto.' },
              ],
            },
          ],
        },
        /* ─────────────────────────── PLAN & FACTURACIÓN (solo CEO) ─────── */
        {
          id: 'plan-facturacion', icon: 'bi-credit-card-2-front-fill', title: 'Plan & Facturación',
          ceoOnly: true,
          description: 'Gestión de la suscripción de tu empresa con POSWAVE. Desde aquí puedes ver tu plan activo, generar la proforma de renovación, subir el comprobante de pago y solicitar un cambio de plan. Exclusivo para CEO.',
          sections: [
            {
              title: 'Qué verás al entrar',
              intro: 'La pantalla muestra el estado actual de la suscripción de tu empresa:',
              steps: [
                { text: 'Plan actual — nombre del plan contratado. Haz clic en esta tarjeta para ver las opciones de cambio de plan.' },
                { text: 'Fecha de vencimiento — cuándo expira el período de servicio actual.' },
                { text: 'Último pago — fecha y monto del pago más reciente aprobado por Perdomo y Asociados.' },
                { text: 'Historial de pagos — tabla con todos tus comprobantes y renovaciones anteriores, con estado (Pendiente / Aprobado / Rechazado).' },
                { text: 'Botón "Subir comprobante" — para enviar el comprobante de tu transferencia bancaria.', tip: 'Si tu empresa está en período de gracia (vencimiento + 5 días), el sistema sigue funcionando con normalidad mientras espera tu pago.' },
              ],
            },
            {
              title: 'Cómo generar la proforma',
              intro: 'La proforma es el documento con el monto exacto a pagar para renovar tu plan:',
              steps: [
                { text: 'En la parte superior de Mi Cuenta verás el botón "Ver proforma" (o aparece un banner si tu plan está por vencer).' },
                { text: 'Haz clic en él. El sistema genera el documento con el precio del plan en Lempiras (calculado al tipo de cambio BCH del día), ISV incluido y los datos de tu empresa.' },
                { text: 'Desde la proforma puedes imprimirla o guardarla como PDF con el botón "Imprimir / Guardar PDF".', tip: 'La proforma incluye la fecha límite de pago y las instrucciones para subir el comprobante al sistema.' },
              ],
            },
            {
              title: 'Cómo subir el comprobante de pago',
              steps: [
                { text: 'Realiza la transferencia bancaria al número de cuenta indicado en la proforma.' },
                { text: 'Ve a Mi Cuenta y haz clic en "Subir comprobante".' },
                { text: 'Selecciona el archivo del comprobante (imagen o PDF).' },
                { text: 'Ingresa el monto transferido y cualquier nota relevante.' },
                { text: 'Haz clic en "Enviar". El comprobante queda en estado Pendiente mientras Perdomo y Asociados lo revisa.', tip: 'Recibirás la aprobación en el historial de esta misma pantalla. Una vez aprobado, tu fecha de vencimiento se extiende automáticamente.' },
                { text: 'Si el comprobante es rechazado, aparecerá con estado Rechazado y el motivo. Sube uno nuevo con la información correcta.', warning: 'El único método de pago aceptado es transferencia bancaria. No se aceptan depósitos.' },
              ],
            },
            {
              title: 'Cómo solicitar un cambio de plan',
              steps: [
                { text: 'En Mi Cuenta, haz clic en la tarjeta "Plan actual". Se abrirá el selector de planes.' },
                { text: 'Verás todos los planes disponibles con sus precios y características. El plan actual aparece resaltado.' },
                { text: 'Si ya hay una solicitud activa, verás un banner indicándolo con la opción de cancelarla.' },
                { text: 'Haz clic en "Seleccionar" en el plan al que deseas cambiar.' },
                { text: 'Confirma el cambio en el modal de confirmación.', tip: 'Tendrás 5 días para enviar el comprobante del nuevo plan. Si no lo envías en ese plazo, la solicitud expira y vuelves al plan anterior.' },
                { text: 'El cambio se hace efectivo cuando Perdomo y Asociados apruebe el comprobante de pago.' },
              ],
              note: 'Solo puede haber una solicitud de cambio de plan activa a la vez. Para cambiar a un plan diferente, primero cancela la solicitud activa y crea una nueva.',
            },
            {
              title: 'Entendiendo los planes (solo CEO)',
              intro: 'Como CEO eres el responsable de la suscripción de tu empresa. Esto es lo que necesitas saber sobre los planes:',
              steps: [
                { text: 'Cada plan tiene un precio en dólares (USD). Al generar la proforma, el sistema convierte ese monto a Lempiras usando el tipo de cambio oficial del Banco Central de Honduras (BCH) del día — el monto en Lempiras puede variar ligeramente entre meses por la fluctuación del tipo de cambio.' },
                { text: 'Los planes pueden ser mensuales o anuales. Un plan anual cubre 12 meses con una sola transferencia. Los planes mensuales se renuevan cada mes.' },
                { text: 'Al entrar a "Plan actual" verás todos los planes disponibles. Cada uno muestra su precio, período y los módulos que incluye. Tu plan actual aparece resaltado en dorado.', tip: 'Si ves un plan con más módulos que necesitas, puedes solicitarlo desde ahí mismo. El cambio aplica una vez que Perdomo y Asociados apruebe el pago.' },
                { text: 'Los módulos que incluye tu plan determinan qué pueden hacer tus empleados: si tu plan no incluye Producción, esa sección no aparece en el sistema aunque el usuario tenga el rol correcto.' },
                { text: 'Si solicitas un cambio a un plan de mayor precio, debes enviar el comprobante del nuevo monto dentro de los 5 días siguientes. Si el plan es más económico, el cambio también requiere aprobación de Perdomo y Asociados.' },
                { text: 'Tu historial de pagos en Mi Cuenta muestra cada renovación — comprobantes que subiste tú y renovaciones directas aplicadas por Perdomo y Asociados — en una sola lista ordenada por fecha.', tip: 'Guarda siempre el PDF de la proforma y el número de transferencia. Si hay alguna discrepancia con Perdomo, esos dos documentos son tu respaldo.' },
              ],
            },
            {
              title: 'El período de gracia y el bloqueo',
              steps: [
                { text: 'Cuando tu plan vence, el sistema NO se bloquea de inmediato. Tienes 5 días calendario de gracia para regularizar el pago.' },
                { text: 'Durante esos 5 días todo funciona con normalidad: ventas, reportes, usuarios — ningún módulo se deshabilita.' },
                { text: 'Al finalizar el día 5 sin pago aprobado, el sistema entra en modo bloqueado. Solo verás la pantalla de plan vencido.', warning: 'El bloqueo aplica a todos los usuarios de tu empresa sin excepción, incluido el Administrador y el CEO. Regulariza el pago antes del día 5 para no interrumpir las operaciones.' },
                { text: 'Una vez que Perdomo y Asociados aprueba tu comprobante, el acceso se restaura automáticamente y la fecha de vencimiento se extiende.' },
              ],
            },
          ],
        },

      ],
    };
  },

  mounted() {
    setPageTitle('Documentación');
  },

  methods: {
    setActive(id) {
      this.activeId = id;
      if (this.$refs.mainEl) this.$refs.mainEl.scrollTop = 0;
    },
    sectionId(title) {
      return 'sec-' + title.replace(/[^a-z0-9]/gi, '-').toLowerCase();
    },
    scrollToSection(title) {
      const el = this.$refs.mainEl?.querySelector('#' + this.sectionId(title));
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap');

* { font-family: 'Montserrat', sans-serif; box-sizing: border-box; margin: 0; padding: 0; }

/* ══════════════════════════════════════
   DESIGN TOKENS — light (default)
   ══════════════════════════════════════ */
.docs-page {
  --dp-bg:          #F4F2EE;
  --dp-sidebar-bg:  #ECEAE5;
  --dp-sidebar-bdr: rgba(160,130,80,.18);
  --dp-head-bdr:    rgba(160,130,80,.14);
  --dp-text:        #1C1A16;
  --dp-muted:       rgba(28,26,22,.45);
  --dp-gold:        #8C6E2F;
  --dp-gold-hi:     #A0812F;
  --dp-gold-pale:   rgba(140,110,47,.08);
  --dp-gold-bdr:    rgba(140,110,47,.22);
  --dp-group-lbl:   rgba(28,26,22,.3);
  --dp-step-bdr:    rgba(255,255,255,.08);
  --dp-chip-bdr:    rgba(140,110,47,.2);
  --dp-sep:         rgba(28,26,22,.07);
  --dp-note-bg:     rgba(60,110,200,.06);
  --dp-note-bdr:    rgba(60,110,200,.14);
  --dp-note-text:   rgba(60,100,200,.85);
  --dp-note-icon:   #4a7ee0;
  --dp-warn-bdr:    rgba(200,100,30,.55);
  --dp-warn-text:   rgba(180,90,20,.95);
  --dp-warn-icon:   #c05818;
  --dp-search-bg:   rgba(0,0,0,.04);
  --dp-scroll:      rgba(140,110,47,.25);
}

/* Dark tokens */
.app-wrapper.dark .docs-page {
  --dp-bg:          #0E0E0E;
  --dp-sidebar-bg:  #111111;
  --dp-sidebar-bdr: rgba(192,157,98,.12);
  --dp-head-bdr:    rgba(192,157,98,.08);
  --dp-text:        #D6D0C8;
  --dp-muted:       rgba(214,208,200,.45);
  --dp-gold:        #C09D62;
  --dp-gold-hi:     #D4B87A;
  --dp-gold-pale:   rgba(192,157,98,.08);
  --dp-gold-bdr:    rgba(192,157,98,.2);
  --dp-group-lbl:   rgba(214,208,200,.25);
  --dp-step-bdr:    rgba(255,255,255,.04);
  --dp-chip-bdr:    rgba(192,157,98,.18);
  --dp-sep:         rgba(255,255,255,.04);
  --dp-note-bg:     rgba(100,160,255,.05);
  --dp-note-bdr:    rgba(100,160,255,.12);
  --dp-note-text:   rgba(160,200,255,.8);
  --dp-note-icon:   #7ab4ff;
  --dp-warn-bdr:    rgba(220,120,50,.6);
  --dp-warn-text:   rgba(220,150,80,.9);
  --dp-warn-icon:   #dc7832;
  --dp-search-bg:   rgba(255,255,255,.04);
  --dp-scroll:      rgba(192,157,98,.25);
}

/* ══════════════════════════════════════
   LAYOUT
   ══════════════════════════════════════ */
.docs-page {
  display: flex;
  height: calc(100vh - 52px);
  background: var(--dp-bg);
  color: var(--dp-text);
  overflow: hidden;
}

/* ════════════════════════════════════
   SIDEBAR
   ════════════════════════════════════ */
.docs-sidebar {
  width: 240px;
  flex-shrink: 0;
  background: var(--dp-sidebar-bg);
  border-right: 1px solid var(--dp-sidebar-bdr);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--dp-scroll) transparent;
}
.docs-sidebar::-webkit-scrollbar { width: 3px; }
.docs-sidebar::-webkit-scrollbar-thumb { background: var(--dp-scroll); border-radius: 2px; }

.sidebar-head {
  padding: 20px 16px 14px;
  border-bottom: 1px solid var(--dp-head-bdr);
}
.sidebar-brand { display: flex; align-items: baseline; letter-spacing: .05em; margin-bottom: 3px; }
.sb-pos  { font-size: 16px; font-weight: 800; color: var(--dp-gold); }
.sb-wave { font-size: 16px; font-weight: 400; color: var(--dp-muted); }
.sidebar-sub {
  display: block; font-size: 10px; font-weight: 600;
  letter-spacing: .1em; text-transform: uppercase; color: var(--dp-group-lbl);
}

/* Search */
.search-wrap { position: relative; padding: 12px 12px 8px; }
.search-icon {
  position: absolute; left: 22px; top: 50%; transform: translateY(-50%);
  color: var(--dp-gold); font-size: 11px; pointer-events: none; opacity: .5;
}
.search-input {
  width: 100%; padding: 8px 28px 8px 30px;
  background: var(--dp-search-bg);
  border: 1px solid var(--dp-gold-bdr);
  border-radius: 8px; color: var(--dp-text); font-size: 12px;
  outline: none; transition: border-color .18s;
}
.search-input:focus { border-color: var(--dp-gold); }
.search-input::placeholder { color: var(--dp-muted); opacity: .6; }
.search-clear {
  position: absolute; right: 20px; top: 50%; transform: translateY(-50%);
  background: none; border: none; color: var(--dp-muted); cursor: pointer;
  font-size: 14px; line-height: 1; transition: color .15s;
}
.search-clear:hover { color: var(--dp-gold); }

/* Nav */
.docs-nav { padding: 6px 0 20px; }
.nav-group-label {
  display: block; padding: 14px 16px 5px;
  font-size: 9.5px; font-weight: 700; letter-spacing: .12em;
  text-transform: uppercase; color: var(--dp-group-lbl); user-select: none;
}
.nav-item {
  display: flex; align-items: center; gap: 10px;
  padding: 7px 12px 7px 16px; cursor: pointer;
  border-left: 2px solid transparent;
  transition: background .15s, border-color .15s;
}
.nav-item:hover { background: var(--dp-gold-pale); border-left-color: var(--dp-gold-bdr); }
.nav-item:hover span { color: var(--dp-gold); }
.nav-item span {
  font-size: 12px; font-weight: 500; color: var(--dp-muted);
  transition: color .15s; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.nav-item.active { background: var(--dp-gold-pale); border-left-color: var(--dp-gold); }
.nav-item.active span { color: var(--dp-gold); font-weight: 700; }
.nav-icon {
  width: 26px; height: 26px; border-radius: 7px;
  background: var(--dp-gold-pale); border: 1px solid var(--dp-gold-bdr);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; font-size: 11px; color: var(--dp-gold); opacity: .7;
  transition: opacity .15s;
}
.nav-item:hover .nav-icon,
.nav-item.active .nav-icon { opacity: 1; }
.no-results { padding: 16px; font-size: 11px; color: var(--dp-muted); font-style: italic; }

/* ════════════════════════════════════
   MAIN CONTENT
   ════════════════════════════════════ */
.docs-main {
  flex: 1; overflow-y: auto;
  padding: 44px 52px 80px;
  scrollbar-width: thin;
  scrollbar-color: var(--dp-scroll) transparent;
}
.docs-main::-webkit-scrollbar { width: 4px; }
.docs-main::-webkit-scrollbar-thumb { background: var(--dp-scroll); border-radius: 2px; }

.docs-module { max-width: 660px; }

/* Module hero */
.mod-hero { display: flex; align-items: flex-start; gap: 20px; margin-bottom: 22px; }
.mod-icon-ring {
  width: 58px; height: 58px; border-radius: 16px;
  background: var(--dp-gold-pale); border: 1px solid var(--dp-gold-bdr);
  display: flex; align-items: center; justify-content: center;
  font-size: 24px; color: var(--dp-gold); flex-shrink: 0;
}
.mod-hero-text { flex: 1; min-width: 0; }
.mod-eyebrow {
  display: inline-block; font-size: 9px; font-weight: 700;
  letter-spacing: .14em; color: var(--dp-gold); opacity: .6; margin-bottom: 5px;
}
.mod-title {
  font-size: 26px; font-weight: 800; color: var(--dp-text);
  line-height: 1.15; margin-bottom: 8px; text-wrap: balance;
}
.mod-desc { font-size: 13px; color: var(--dp-muted); line-height: 1.65; max-width: 520px; }

/* Quick-jump chips */
.section-chips {
  display: flex; flex-wrap: wrap; gap: 7px;
  margin-bottom: 38px; padding-bottom: 28px;
  border-bottom: 1px solid var(--dp-sep);
}
.section-chip {
  padding: 5px 13px; border-radius: 20px;
  border: 1px solid var(--dp-chip-bdr);
  background: transparent; color: var(--dp-gold);
  opacity: .7; font-size: 11px; font-weight: 600;
  cursor: pointer; transition: opacity .15s, background .15s;
}
.section-chip:hover { opacity: 1; background: var(--dp-gold-pale); }

/* Sections */
.docs-section.not-first { margin-top: 48px; padding-top: 40px; border-top: 1px solid var(--dp-sep); }
.section-header { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; }
.section-title { font-size: 15px; font-weight: 700; color: var(--dp-text); flex: 1; text-wrap: balance; }
.section-badge {
  font-size: 10px; font-weight: 700; color: var(--dp-gold);
  background: var(--dp-gold-pale); border: 1px solid var(--dp-gold-bdr);
  padding: 3px 10px; border-radius: 20px; white-space: nowrap; letter-spacing: .02em;
}
.section-intro {
  font-size: 13px; color: var(--dp-muted); line-height: 1.65;
  padding-left: 14px; border-left: 2px solid var(--dp-gold-bdr);
  margin-bottom: 22px; font-style: italic;
}

/* Timeline */
.steps-list { display: flex; flex-direction: column; }
.step-item  { display: flex; gap: 18px; }
.step-track { display: flex; flex-direction: column; align-items: center; flex-shrink: 0; width: 34px; }
.step-num {
  width: 34px; height: 34px; border-radius: 50%;
  background: linear-gradient(135deg, var(--dp-gold) 0%, var(--dp-gold-hi) 100%);
  color: #fff;
  font-size: 12px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; box-shadow: 0 2px 10px rgba(0,0,0,.15);
  position: relative; z-index: 1;
}
.app-wrapper.dark .docs-page .step-num { color: #0e0e0e; }
.step-line {
  flex: 1; width: 1px; min-height: 16px; margin: 4px 0;
  background: linear-gradient(to bottom, var(--dp-gold-bdr), transparent);
}
.step-body { flex: 1; min-width: 0; padding-bottom: 22px; padding-top: 6px; }
.step-item:last-child .step-body { padding-bottom: 0; }
.step-text { font-size: 13.5px; color: var(--dp-text); line-height: 1.7; }

/* Callouts */
.callout {
  display: flex; align-items: flex-start; gap: 9px;
  margin-top: 10px; padding: 2px 0 2px 14px;
  font-size: 12px; line-height: 1.6; font-style: italic;
}
.callout i { font-size: 11px; flex-shrink: 0; margin-top: 2px; font-style: normal; }
.callout.tip { border-left: 2px solid var(--dp-gold-bdr); color: var(--dp-gold); }
.callout.tip i { color: var(--dp-gold); }
.callout.warn { border-left: 2px solid var(--dp-warn-bdr); color: var(--dp-warn-text); }
.callout.warn i { color: var(--dp-warn-icon); }

/* Section note */
.section-note {
  display: flex; align-items: flex-start; gap: 9px;
  margin-top: 22px; padding: 11px 16px;
  background: var(--dp-note-bg); border: 1px solid var(--dp-note-bdr);
  border-radius: 10px; font-size: 12px; color: var(--dp-note-text); line-height: 1.6;
}
.section-note i { color: var(--dp-note-icon); flex-shrink: 0; margin-top: 2px; }

/* Responsive */
@media (max-width: 900px) { .docs-main { padding: 28px 28px 60px; } }
@media (max-width: 768px) {
  .docs-page    { flex-direction: column; height: auto; overflow: visible; }
  .docs-sidebar { width: 100%; height: auto; max-height: 260px; border-right: none; border-bottom: 1px solid var(--dp-sidebar-bdr); }
  .docs-main    { padding: 24px 20px 48px; overflow: visible; }
}
</style>
